import { useEffect, useRef, useState, type Ref, type RefObject } from 'react';
import {
  huddleReactions,
  sameBreakoutGroup,
  type HuddleParticipant,
  type HuddleRequestKind,
  type QuickVote,
} from '@avslack/shared';
import * as ContextMenu from '@radix-ui/react-context-menu';
import type { Track } from 'livekit-client';
import { OFFLINE_CALL_FAILED, useHuddle } from '../huddle/HuddleProvider';
import { PREFLIGHT_LABELS } from '../huddle/preflight';
import { MAX_VOLUME } from '../huddle/participant-volume';
import { NOISE_LEVEL_LABEL, NOISE_LEVELS } from '../huddle/noise';
import { useUsers } from '../data/users';
import { useSession } from '../lib/session';
import { setHuddleHost, setTestHuddle } from '../lib/api';
import { Avatar } from './Avatar';
import { ScreenShareView } from './ScreenShareView';
import { closeHuddleWindow, huddleInline } from '../lib/huddle-window';
import { rememberedMonitor } from '../lib/share-frame';
import { HuddleTimer } from './HuddleTimer';
import { Icon, type IconName } from './Icon';
import { PhoneDialer, PhoneLegs, useShowDialer } from './PhoneLegs';
import { BackgroundPicker } from './BackgroundPicker';
import { CustomerPanel } from './CustomerPanel';
import { HuddleChat } from './HuddleChat';
import { HuddleNotes } from './HuddleNotes';
import { TestScriptReader } from './TestScriptReader';
import { useDismiss } from '../lib/dismiss';
import { HuddleGuests } from './HuddleGuests';
import { GuestLinkDialog } from './GuestLinkDialog';
import { StopwatchControl, TimerAlarm, TimerTile } from './HuddleStopwatch';
import { HuddleMoreSheet, MoreSection, type MoreTile } from './HuddleMoreSheet';
import { useIsDesktop } from '../lib/media';
import { HuddlePollPanel, HuddlePollStartDialog } from './HuddlePoll';
import {
  BreakoutBar,
  BreakoutHelpBanner,
  BreakoutHostPanel,
  BreakoutStartDialog,
} from './HuddleBreakouts';

/**
 * A huddle sajat ablakban.
 *
 * A meglevo megbizhatosagi szabalyok VALTOZATLANOK: a kilepes feltetel
 * nelkuli, a ketszeri Esc veszkijarat mukodik, a resztvevo-lista a szerver
 * adatbazisa. Ez a nezet csak nagyobb - nem mas.
 */
export function HuddleRoom({
  meId,
  onMinimize,
}: {
  meId: string;
  /** Ha meg van adva, a fejlecben "Kicsinyítés" gomb jelenik meg (vevoszolgalati mod). */
  onMinimize?: (() => void) | undefined;
}) {
  const huddle = useHuddle();
  const { state } = huddle;

  if (state === 'preflight' || state === 'connecting') {
    return <Connecting state={state} />;
  }

  if (state === 'idle' || state === 'failed' || !huddle.huddle) {
    return <Ended error={huddle.error} />;
  }

  return <Live meId={meId} onMinimize={onMinimize} />;
}

// ─── Csatlakozas ──────────────────────────────────────────────────────────────

function Connecting({ state }: { state: 'preflight' | 'connecting' }) {
  const { preflight, leave } = useHuddle();

  return (
    <div className="grid h-full place-items-center bg-surface p-6">
      <div className="w-full max-w-sm text-center">
        <div
          aria-hidden
          className="mx-auto mb-4 grid size-14 animate-pulse place-items-center rounded-2xl bg-accent-soft text-2xl"
        >
          <Icon name="huddle" />
        </div>
        <p className="font-display text-lg font-semibold">
          {state === 'preflight' ? 'Előellenőrzés…' : 'Csatlakozás…'}
        </p>
        <p className="mt-1 text-sm text-ink-muted">
          Megnézzük, hogy minden készen áll-e, mielőtt belépnél.
        </p>

        <ul className="mx-auto mt-4 w-fit space-y-1.5 text-left">
          {(['microphone', 'devices', 'server'] as const).map((id) => {
            const check = preflight?.checks.find((item) => item.id === id);
            return (
              <li key={id} className="flex items-center gap-2 text-xs">
                <span
                  aria-hidden
                  className={
                    check === undefined
                      ? 'size-1.5 animate-pulse rounded-full bg-ink-faint'
                      : check.ok
                        ? 'size-1.5 rounded-full bg-[var(--presence-active)]'
                        : 'size-1.5 rounded-full bg-[var(--danger)]'
                  }
                />
                <span className={check?.ok === false ? 'text-ink' : 'text-ink-muted'}>
                  {PREFLIGHT_LABELS[id]}
                </span>
              </li>
            );
          })}
        </ul>

        {/* Meg csatlakozas kozben is ki lehet szallni - ez nem opcionalis. */}
        <button
          type="button"
          onClick={leave}
          className="mt-5 rounded-control px-3 py-1.5 text-xs text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
        >
          Mégsem
        </button>
      </div>
    </div>
  );
}

function Ended({ error }: { error: string | null }) {
  /**
   * Rendben lezart hivas utan az ablak MAGATOL bezarodik.
   *
   * Aki kilepett, az mar tovabblepett - egy "Ablak bezarasa" gomb csak egy
   * felesleges kattintas lenne. HIBA eseten viszont marad nyitva: ott van mit
   * elolvasni, es a felhasznalo donti el, mikor zarja be.
   */
  useEffect(() => {
    if (error) return;
    const timer = setTimeout(() => void closeHuddleWindow(), 400);
    return () => clearTimeout(timer);
  }, [error]);

  return (
    <div className="grid h-full place-items-center bg-surface p-6">
      <div className="w-full max-w-sm text-center">
        <p className="font-display text-lg font-semibold">
          {error?.startsWith(OFFLINE_CALL_FAILED)
            ? 'A hívás nem indult el'
            : error
              ? 'Az ölelés megszakadt'
              : 'Az ölelés véget ért'}
        </p>
        {error && (
          <p className="mt-2 rounded-control border border-danger/30 bg-danger-soft px-3 py-2.5 text-sm text-danger">
            {error}
          </p>
        )}
        {/*
          A gomb megmarad: ha a bezaras nem sikerul (a bongeszo csak
          szkriptbol nyitott ablakot enged bezarni), ne ragadjon itt a
          felhasznalo.
        */}
        <button
          type="button"
          onClick={() => void closeHuddleWindow()}
          className="mt-5 rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink"
        >
          {huddleInline() ? 'Vissza' : 'Ablak bezárása'}
        </button>
      </div>
    </div>
  );
}

// ─── Elo hivas ────────────────────────────────────────────────────────────────

function Live({ meId, onMinimize }: { meId: string; onMinimize?: (() => void) | undefined }) {
  const {
    huddle,
    breakout,
    breakoutEndsAtMs,
    helpRequests,
    requestBreakoutHelp,
    dismissHelp,
    state,
    speakingIds,
    isMuted,
    isCameraOn,
    isCameraBusy,
    isPaused,
    background,
    isSharingScreen,
    screenShares,
    visibleShares,
    shareFrame,
    shareMonitors,
    chooseShareMonitor,
    cameraTracks,
    devices,
    activeDeviceId,
    cancelInvite,
    outputDevices,
    activeOutputId,
    videoDevices,
    activeCameraId,
    canChooseOutput,
    leave,
    toggleMute,
    togglePause,
    toggleCamera,
    setBackground,
    toggleScreenShare,
    switchScreenShare,
    switchDevice,
    switchOutput,
    switchCamera,
    toggleShare,
    sendReaction,
    raisedHands,
    toggleHand,
    quickVotes,
    castQuickVote,
    clearQuickVotes,
    awayIds,
    toggleAway,
    muteOther,
    requestFrom,
    incomingRequest,
    answerRequest,
    requestNotice,
    dismissRequestNotice,
    participantVolumes,
    setParticipantVolume,
    chatSignal,
    isTranscribing,
    isRecording,
    toggleTranscribe,
    isAudioBlocked,
    enableAudio,
    error,
    dismissError,
  } = useHuddle();

  const { nameOf } = useUsers();
  /** Kolléga VAGY vendeg neve a LiveKit-identitasbol (a vendeg nem felhasznalo - H2). */
  const whoIs = (identity: string) =>
    huddle?.guests?.find((guest) => guest.identity === identity)?.name ?? nameOf(identity);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [reactionsOpen, setReactionsOpen] = useState(false);
  const [backgroundOpen, setBackgroundOpen] = useState(false);
  const backgroundButtonRef = useRef<HTMLButtonElement | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [guestLinkOpen, setGuestLinkOpen] = useState(false);
  /** Kiscsoportos szobak (docs/19): az inditas ablaka es a hazigazda attekintoje. */
  const [breakoutStartOpen, setBreakoutStartOpen] = useState(false);
  const [breakoutPanelOpen, setBreakoutPanelOpen] = useState(true);
  /**
   * KESKENY KEPERNYO (telefon): a masodlagos funkciok a "Tobb" lapra kerulnek, a
   * fejlecben es a vezerlosavban csak az marad, amit hivas kozben folyton nyomunk.
   */
  const compact = !useIsDesktop();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement | null>(null);
  /** Szavazas az olelesben: az inditas ablaka es a nyitott szavazas panelje. */
  const [pollStartOpen, setPollStartOpen] = useState(false);
  const [pollPanelOpen, setPollPanelOpen] = useState(true);
  const openPollId = huddle?.poll?.id ?? null;
  // Egy UJ szavazas magatol megnyilik: aki bent ul, annak szolnia kell a kerdesnek.
  useEffect(() => {
    if (openPollId) setPollPanelOpen(true);
  }, [openPollId]);
  const [chatOpen, setChatOpen] = useState(false);
  /** A hivas kozos jegyzete (versenyaudit H4) - oldalpanel. */
  const [notesOpen, setNotesOpen] = useState(false);
  const reactionsRef = useRef<HTMLDivElement | null>(null);
  const reactionsButtonRef = useRef<HTMLButtonElement | null>(null);
  const devicesRef = useRef<HTMLDivElement | null>(null);
  const devicesButtonRef = useRef<HTMLButtonElement | null>(null);
  const inviteButtonRef = useRef<HTMLButtonElement | null>(null);
  useDismiss(reactionsRef, () => setReactionsOpen(false), {
    enabled: reactionsOpen,
    ignore: [reactionsButtonRef],
    returnFocus: reactionsButtonRef,
  });
  useDismiss(devicesRef, () => setDevicesOpen(false), {
    enabled: devicesOpen,
    ignore: [devicesButtonRef],
    returnFocus: devicesButtonRef,
  });
  /** A teszthivas felolvasando szovege (atirat-meres) - oldalpanelben. */
  const [scriptOpen, setScriptOpen] = useState(false);
  /*
    UGYFEL-PANEL: telefonhivasnal MAGATOL nyilik.

    Nem kenyelmi dontes: a kolléga epp most vette fel a telefont, es pont akkor
    kell latnia, kivel beszel es mi all a jegyzetekben - nem egy kattintassal
    kesobb. Bezarni barmikor lehet, es az allapot a hivasra szol.
  */
  const [customerOpen, setCustomerOpen] = useState(
    /*
      Keskeny kepernyon NEM nyilik magatol: ott a panel `w-full`, tehat eltakarna
      a hivas tobbi reszet abban a pillanatban, amikor a kolléga felveszi. Szeles
      kepernyon viszont pont ez kell - a gomb mindket esetben ott van.
    */
    () => typeof window !== 'undefined' && window.innerWidth >= 640,
  );

  /*
    MELYIK ugyfelrol van szo: az elso telefonos ag szama.

    Egy hivasban tobb ag is lehet (atadas, visszahivas), de ugyfel egy van -
    ugyanaz a szabaly, mint a kartyanal es a jegyzetnel. `live`: tart-e meg a
    beszelgetes - ettol fugg, hogy az atirat teljes-e.
  */
  const phoneNumber = huddle?.phoneLegs.find((leg) => leg.callerNumber)?.callerNumber ?? null;
  const livePhone = huddle?.phoneLegs.some((leg) => leg.state !== 'ended') ?? false;

  /*
    Olvasatlan chat-jelzes.

    A hivas kozben a chat gyakran zarva van - ha semmi nem jelezne, hogy irtak,
    a panel eppen azt a celjat vesztene el, hogy a fontos dolgok atmenjenek.
    A szamlalot a media-adatcsatorna jelzese novel; a megnyitas nullazza.
  */
  const [seenChatSignal, setSeenChatSignal] = useState(0);
  const hasUnreadChat = !chatOpen && chatSignal > seenChatSignal;

  const openChat = () => {
    setSeenChatSignal(chatSignal);
    setChatOpen(true);
  };

  /*
    MAS NEMITOTT LE - ki kell mondani, nagyon lathatoan.

    Enelkul csak a mikrofongomb valt at, es az ember beszel tovabb abban a
    hitben, hogy hallják. A `mutedBy` a szerverrol jon; a visszakapcsolaskor a
    szerver torli, de addig - egy eletjelnyi ideig - meg a regi ertek allhat
    ott. Az `acked` azt jegyzi meg, melyik lenemitast intezte mar el a
    felhasznalo (visszakapcsolt, vagy tudomasul vette), hogy az overlay ne
    villanjon fel ujra.
  */
  const myMutedBy = huddle?.participants.find((p) => p.user.id === meId)?.mutedBy ?? null;
  const [ackedMutedBy, setAckedMutedBy] = useState<string | null>(null);
  useEffect(() => {
    if (myMutedBy === null) setAckedMutedBy(null);
  }, [myMutedBy]);
  useEffect(() => {
    // Barhogy kapcsolt vissza (overlay, gomb, Ctrl+M), ez a lenemitas elintezve.
    if (!isMuted && myMutedBy) setAckedMutedBy(myMutedBy);
  }, [isMuted, myMutedBy]);
  const mutedByOther =
    isMuted && !isPaused && myMutedBy !== null && myMutedBy !== meId && ackedMutedBy !== myMutedBy
      ? myMutedBy
      : null;

  const showDialer = useShowDialer(huddle, meId);

  if (!huddle) return null;

  /*
    KISCSOPORTOS IDOSZAK: csak a sajat csoportunkat latjuk a racsban.

    A foszoba (nem beosztottak, telefon) szinten egy "csoport": ott egymast es a
    telefonos hivot latjak. A varakozo vendeg a hazigazdak/mindenki dolga, ezert
    az sosem rejtodik el - az beengedesre var.
  */
  const isHost = huddle.hostId === meId;
  const inMyGroup = (key: string) => sameBreakoutGroup(breakout, meId, key);
  const shownParticipants = breakout
    ? huddle.participants.filter((p) => inMyGroup(p.user.id))
    : huddle.participants;
  const shownHuddle = breakout
    ? {
        ...huddle,
        guests: huddle.guests.filter((g) => g.state !== 'admitted' || inMyGroup(g.identity)),
        phoneLegs: inMyGroup('') ? huddle.phoneLegs : [],
      }
    : huddle;

  /*
    Amit EPPEN nezunk - tobb is lehet.

    A sorrendet a bekapcsolas adja (`visibleShares`), nem a megosztasok
    erkezesi sorrendje: aki masodikkent kapcsolta be, az kerul jobbra, es a kep
    nem ugral at, amikor egy harmadik is beszall.
  */
  /*
    A jelentkezok SORRENDBEN - aki elobb emelte fel a kezet, az elobb.

    A csempek NEM rendezodnek at emiatt: egy ugralo racsban senki nem talalna
    meg senkit. A sorszam a csempen all.
  */
  const handQueue = Object.entries(raisedHands)
    .sort(([, a], [, b]) => a - b)
    .map(([identity]) => identity);
  const isHandRaised = handQueue.includes(meId);

  // Gyorsszavazas: kiscsoportban csak a sajat csoporte szamit.
  const groupVotes = Object.entries(quickVotes).filter(([identity]) => inMyGroup(identity));
  const myVote = quickVotes[meId] ?? null;
  const isAway = Boolean(awayIds[meId]);

  const lathato = visibleShares
    .map((id) => screenShares.find((share) => share.identity === id))
    .filter((share): share is NonNullable<typeof share> => share !== undefined);

  /*
    Miert nincs zold keret magan a megosztott KIJELZON?

    Ezt ki kell mondani. Aki kerte a keretet es nem latja, az elrontottnak
    hiszi a funkciot - nem annak, hogy a bongeszo nem tud a sajat ablakan
    kivulre rajzolni.
  */
  const frameNote =
    shareFrame === 'browser'
      ? 'A kijelző szélén csak az asztali alkalmazásban jelenik meg a zöld keret.'
      : null;

  /*
    A "TOBB" LAP csempei (telefon). A gyakoriak elol; ami nem ervenyes (pl. ugyfel
    nem telefonhivasnal), nem jelenik meg - egy ures csempe csak zaj lenne.
  */
  const moreTiles: MoreTile[] = compact
    ? [
        {
          key: 'hand',
          icon: 'kez',
          label: isHandRaised ? 'Kéz leengedése' : 'Jelentkezés',
          active: isHandRaised,
          onSelect: toggleHand,
        },
        {
          key: 'poll',
          icon: 'csillag',
          label: huddle.poll ? 'Szavazás' : 'Szavazás indítása',
          active: Boolean(huddle.poll),
          badge: Boolean(huddle.poll) && !pollPanelOpen,
          onSelect: () => (huddle.poll ? setPollPanelOpen(true) : setPollStartOpen(true)),
        },
        {
          key: 'vote-yes',
          icon: 'pipa',
          label: myVote === 'yes' ? 'Pipa visszavonása' : 'Egyetértek (pipa)',
          active: myVote === 'yes',
          onSelect: () => castQuickVote('yes'),
        },
        {
          key: 'vote-no',
          icon: 'bezaras',
          label: myVote === 'no' ? 'X visszavonása' : 'Nem értek egyet (X)',
          active: myVote === 'no',
          onSelect: () => castQuickVote('no'),
        },
        {
          key: 'away',
          icon: 'kave',
          label: isAway ? 'Visszajöttem' : 'Mindjárt jövök',
          active: isAway,
          onSelect: toggleAway,
        },
        {
          key: 'pause',
          icon: isPaused ? 'folytatas' : 'szunet',
          label: isPaused ? 'Vissza a hívásba' : 'Szünet',
          active: isPaused,
          onSelect: () => void togglePause(),
        },
        {
          key: 'transcribe',
          icon: 'atirat',
          label: isTranscribing ? 'Átirat kikapcsolása' : 'Átirat bekapcsolása',
          active: isTranscribing,
          onSelect: () => void toggleTranscribe(),
        },
        {
          key: 'notes',
          icon: 'canvas',
          label: 'Jegyzetvászon',
          active: notesOpen,
          onSelect: () => setNotesOpen((open) => !open),
        },
        {
          key: 'invite',
          icon: 'meghivas',
          label: 'Behívás',
          onSelect: () => setInviteOpen(true),
        },
        {
          key: 'guest',
          icon: 'link',
          label: 'Vendég meghívása',
          onSelect: () => setGuestLinkOpen(true),
        },
        ...(isHost
          ? [
              {
                key: 'breakouts',
                icon: 'tagok' as const,
                label: breakout ? 'Kiscsoportok kezelése' : 'Kiscsoportok',
                active: Boolean(breakout),
                onSelect: () =>
                  breakout ? setBreakoutPanelOpen(true) : setBreakoutStartOpen(true),
              },
            ]
          : []),
        ...(isCameraOn
          ? [
              {
                key: 'background',
                icon: 'rejt' as const,
                label: 'Kamera háttere',
                active: background.kind !== 'none',
                onSelect: () => setBackgroundOpen(true),
              },
            ]
          : []),
        ...(isSharingScreen
          ? [
              {
                key: 'switch-share',
                icon: 'kepernyo' as const,
                label: 'Másik ablak megosztása',
                onSelect: () => void switchScreenShare(),
              },
            ]
          : []),
        ...(phoneNumber
          ? [
              {
                key: 'customer',
                icon: 'ugyfel' as const,
                label: 'Ügyfél',
                active: customerOpen,
                onSelect: () => setCustomerOpen((open) => !open),
              },
            ]
          : []),
        ...(huddle.audioKeepUntil
          ? [
              {
                key: 'script',
                icon: 'atirat' as const,
                label: 'Felolvasandó szöveg',
                active: scriptOpen,
                onSelect: () => setScriptOpen((open) => !open),
              },
            ]
          : []),
      ]
    : [];
  return (
    <div className="flex h-full flex-col bg-surface">
      {/* ─── Fejlec ───────────────────────────────────────────────────────── */}
      <header
        className={[
          '@container/fej relative flex items-center border-b border-border-soft',
          compact ? 'gap-2 px-3 py-2' : 'gap-3 px-4 py-2.5',
        ].join(' ')}
      >
        <span aria-hidden className="text-base">
          <Icon name="huddle" />
        </span>
        <div className="min-w-0 overflow-hidden">
          <p className="truncate font-display text-sm font-semibold">{huddle.title ?? 'Ölelés'}</p>
          <p className="flex items-center gap-1.5 overflow-hidden whitespace-nowrap text-2xs text-ink-faint">
            <HuddleTimer startedAt={huddle.startedAt} className="font-medium text-ink-muted" />
            <span aria-hidden>·</span>
            <span>
              {huddle.participants.filter((p) => p.state === 'joined').length} fő
              {compact ? '' : ' bent'}
              {state === 'reconnecting' && ' · Újracsatlakozás…'}
            </span>
            {handQueue.length > 0 && (
              <>
                <span aria-hidden>·</span>
                <span
                  title={`Jelentkezik: ${handQueue.map((id) => (id === meId ? 'te' : nameOf(id))).join(', ')}`}
                  className="flex items-center gap-1 rounded-pill bg-[oklch(84%_0.17_85)] px-1.5 font-semibold text-[oklch(28%_0.06_60)]"
                >
                  <Icon name="kez" /> {handQueue.length}
                  <span className="hidden sm:inline">jelentkezik</span>
                </span>
              </>
            )}
          </p>
        </div>

        {/*
          A MEGOSZTAS-KAPCSOLOK es a TARCSAZO a fejlecben allnak, nem a
          resztvevok felett: ott kulon sorokat foglaltak, es megosztas kozben a
          kepernyo elol vettek el a helyet.

          KAPCSOLOK, nem valtogomb. Minden megosztashoz egy gomb, ami be- vagy
          kikapcsolja. Korabban ez valto volt: a masodikra kattintva az elso
          eltunt, tehat ket kepernyot egyszerre nezni nem lehetett. Egy gomb
          mar akkor is latszik, ha csak egy megosztas van - igy ki is lehet
          kapcsolni, hogy a resztvevok kepe keruljon elore.
        */}
        {!compact && (screenShares.length > 0 || showDialer) && (
          <div className="flex min-w-0 items-center gap-1.5">
            {/* A gorgetes CSAK a megosztasokra: a tarcsazo legordulojet levagna. */}
            <div className="flex min-w-0 items-center gap-1.5 overflow-x-auto">
              {screenShares.map((share) => {
                const bekapcsolva = visibleShares.includes(share.identity);
                const nev = share.identity === meId ? 'Te osztod meg' : whoIs(share.identity);

                return (
                  <button
                    key={share.identity}
                    type="button"
                    onClick={() => toggleShare(share.identity)}
                    aria-pressed={bekapcsolva}
                    title={
                      bekapcsolva ? `${nev} — kattints az elrejtéshez` : `${nev} megjelenítése`
                    }
                    className={`shrink-0 whitespace-nowrap rounded-control px-2.5 py-1 text-xs transition-colors ${
                      bekapcsolva
                        ? 'bg-accent font-semibold text-accent-ink'
                        : 'bg-surface-sunken text-ink-muted hover:text-ink'
                    }`}
                  >
                    <Icon name="kepernyo" /> {nev}
                  </button>
                );
              })}
            </div>
            {showDialer && <PhoneDialer huddleId={huddle.id} channelId={huddle.channelId} />}
          </div>
        )}

        {/*
          AZ ATIRAT JELZESE - mindenkinek, elteveszthetetlenul.

          Aki bent ul egy hivasban, annak joga van tudni, hogy felvetel keszul
          rola. Ez nem UI-kenyelem, hanem a funkcio feltetele: egy titokban
          keszulo felvetel nem opcio. Ezert all a fejlecben, allandoan, es
          ezert latszik kulon, ha eppen a MI mikrofonunk rogzit.
        */}
        {isTranscribing && (
          <span
            title={
              isRecording
                ? 'Átirat készül — a mikrofonod éppen rögzít'
                : 'Átirat készül erről a hívásról'
            }
            className="ml-auto flex shrink-0 items-center gap-1.5 rounded-pill bg-[var(--danger-soft)] px-2 py-1 text-2xs font-medium text-ink"
          >
            <span
              aria-hidden
              className={`size-1.5 rounded-full bg-[var(--danger)]${
                isRecording ? ' animate-pulse' : ' opacity-50'
              }`}
            />
            Átirat
            {!compact && <span className="@max-[80rem]/fej:hidden"> készül</span>}
          </span>
        )}

        {/*
          UGYFEL: csak telefonhivasnal van ertelme, ezert csak ott latszik.
          Egy gomb, ami a hivasok felenel semmit nem nyitna meg, rosszabb,
          mintha ott sem lenne.
        */}
        <div className={`flex shrink-0 items-center gap-1.5${isTranscribing ? '' : ' ml-auto'}`}>
          {/* Kozos stopper/idozito (a hazigazda kezeli; ki is nyithatja). */}
          {!compact && <StopwatchControl huddle={huddle} meId={meId} />}
          <TestCallBadge huddleId={huddle.id} keepUntil={huddle.audioKeepUntil ?? null} />
          {!compact && phoneNumber && (
            <HeaderAction
              icon="ugyfel"
              label="Ügyfél"
              active={customerOpen}
              onClick={() => setCustomerOpen((open) => !open)}
            />
          )}

          {/* A felolvasando szoveg csak teszthivasnal kell - akkor viszont kezre. */}
          {!compact && huddle.audioKeepUntil && (
            <HeaderAction
              icon="atirat"
              label="Szöveg"
              active={scriptOpen}
              onClick={() => setScriptOpen((open) => !open)}
            />
          )}

          {!compact && (
            <HeaderAction
              icon="canvas"
              label="Jegyzetvászon"
              active={notesOpen}
              onClick={() => setNotesOpen((open) => !open)}
            />
          )}

          {/* Szavazas: barki inditja; nyitott szavazasnal a panelt kapcsolja. */}
          {!compact && (
            <HeaderAction
              icon="csillag"
              label="Szavazás"
              active={huddle.poll ? pollPanelOpen : pollStartOpen}
              badge={Boolean(huddle.poll) && !pollPanelOpen}
              onClick={() =>
                huddle.poll ? setPollPanelOpen((open) => !open) : setPollStartOpen(true)
              }
            />
          )}

          {/* Kiscsoportos szobak: csak a hazigazdanak (docs/19). */}
          {!compact && isHost && (
            <HeaderAction
              icon="tagok"
              label="Kiscsoportok"
              active={breakout ? breakoutPanelOpen : breakoutStartOpen}
              onClick={() =>
                breakout ? setBreakoutPanelOpen((open) => !open) : setBreakoutStartOpen(true)
              }
            />
          )}

          {!compact && (
            <HeaderAction
              icon="link"
              label="Vendég"
              active={guestLinkOpen}
              onClick={() => setGuestLinkOpen(true)}
            />
          )}

          <HeaderAction
            icon="chat"
            label={breakout ? 'Chat (a kiscsoportos időre zárolva)' : 'Chat'}
            active={chatOpen && !breakout}
            badge={hasUnreadChat && !breakout}
            disabled={Boolean(breakout)}
            onClick={() => (chatOpen ? setChatOpen(false) : openChat())}
          />

          {/* A behivas a fejlec FO cselekvese - kitoltott gomb, nem csak keret. */}
          {!compact && (
            <HeaderAction
              ref={inviteButtonRef}
              icon="meghivas"
              label="Behívás"
              primary
              active={inviteOpen}
              onClick={() => setInviteOpen((open) => !open)}
            />
          )}

          {/* Telefonon minden masodlagos funkcio ezen a lapon van. */}
          {compact && (
            <HeaderAction
              ref={moreButtonRef}
              icon="szerkesztesTobb"
              label="Több"
              active={moreOpen}
              badge={(Boolean(huddle.poll) && !pollPanelOpen) || helpRequests.length > 0}
              onClick={() => setMoreOpen(true)}
            />
          )}

          {/* Vevoszolgalati modban a fo ablak teljes nezete visszacsukhato a lebego panelre. */}
          {onMinimize && <HeaderAction icon="le" label="Kicsinyítés" onClick={onMinimize} />}
        </div>

        {inviteOpen && (
          <InvitePanel
            triggerRef={compact ? moreButtonRef : inviteButtonRef}
            onClose={() => setInviteOpen(false)}
          />
        )}
        <GuestLinkDialog
          channelId={huddle.channelId}
          open={guestLinkOpen}
          onOpenChange={setGuestLinkOpen}
        />
        <HuddlePollStartDialog
          open={pollStartOpen}
          onOpenChange={setPollStartOpen}
          huddleId={huddle.id}
        />
        {isHost && (
          <BreakoutStartDialog
            open={breakoutStartOpen}
            onOpenChange={setBreakoutStartOpen}
            huddle={huddle}
          />
        )}
      </header>

      {/* Telefonon a futo stopper/idozito kulon, keskeny sorban all (a fejlecbe nem fer). */}
      {compact && huddle.timer && (
        <div className="flex justify-center border-b border-border-soft px-3 py-1.5">
          <StopwatchControl huddle={huddle} meId={meId} />
        </div>
      )}

      {/* KISCSOPORTOS IDOSZAK: mindenkinek, a fejlec alatt - a visszaszamlalo ott all. */}
      {breakout && (
        <BreakoutBar
          huddle={huddle}
          breakout={breakout}
          endsAtMs={breakoutEndsAtMs}
          meId={meId}
          onHelp={isHost ? undefined : requestBreakoutHelp}
        />
      )}
      {isHost && breakout && (
        <BreakoutHelpBanner huddle={huddle} requests={helpRequests} onDismiss={dismissHelp} />
      )}

      {/*
        SZUNET - allando, elteveszthetetlen jelzes.

        Aki szuneten van, az mashol jar: a hivas ablaka a hatterben lehet, es
        percek mulva ternek vissza hozza. Egy kicsi ikon a vezerlosavon ehhez
        keves - ha nem latszik azonnal, hogy "ez a hivas most nema", a
        felhasznalo beszelni kezd, es senki nem hallja. Ez ugyanaz a nema
        kudarc, ami miatt a projekt elindult.
      */}
      {isPaused && (
        <div className="flex items-center gap-3 border-b border-border-soft bg-accent-soft px-4 py-2">
          <span aria-hidden className="text-base text-accent">
            <Icon name="szunet" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-ink">Szüneten vagy</p>
            <p className="text-2xs text-ink-muted">
              Nem hallanak, és te sem hallod őket. Addig is beléphetsz egy másik ölelésbe.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void togglePause()}
            className="shrink-0 rounded-control bg-accent px-3 py-1.5 text-xs font-semibold text-accent-ink transition-colors hover:bg-accent-hover"
          >
            Vissza a hívásba
          </button>
        </div>
      )}

      {/*
        A BONGESZO LETILTOTTA A HANGOT.

        Ilyenkor a hivas el es a resztvevok latszanak, csak eppen nem hallatszik
        semmi - ugy nez ki, mintha minden rendben lenne. Ezert nem elvetheto
        hibasav, hanem egy gomb: a bongeszo pontosan egy kattintast var.
      */}
      {isAudioBlocked && (
        <div
          role="alert"
          className="flex items-center gap-3 border-b border-border-soft bg-[var(--danger-soft)] px-4 py-2"
        >
          <p className="min-w-0 flex-1 text-sm text-ink">
            A böngésző letiltotta a hangot — így nem hallod a többieket.
          </p>
          <button
            type="button"
            onClick={() => void enableAudio()}
            className="shrink-0 rounded-control bg-accent px-3 py-1.5 text-xs font-semibold text-accent-ink transition-colors hover:bg-accent-hover"
          >
            Hang bekapcsolása
          </button>
        </div>
      )}

      {/*
        Hiba KOZBEN - nem a hivas vegen.
        A kamera, a megosztas vagy egy elnemitas elbukhat ugy is, hogy a hivas
        maga tovabb el. Ha ezt nem mondanank meg, a felhasznalo csak annyit
        latna, hogy "nem tortent semmi" - eppen az a nema kudarc, ami miatt a
        Slack huddle-je hasznalhatatlanna valt nekunk.
      */}
      {error && (
        <div
          role="alert"
          className="flex items-center gap-3 border-b border-border-soft bg-[var(--danger-soft)] px-4 py-2"
        >
          <p className="min-w-0 flex-1 text-sm text-ink">{error}</p>
          <button
            type="button"
            onClick={dismissError}
            className="shrink-0 rounded-control bg-surface-raised px-2.5 py-1 text-xs font-medium"
          >
            Értem
          </button>
        </div>
      )}

      {/*
        A SAJAT kereseink sorsa: elment, elfogadtak, elutasitottak.
        Enelkul a kero csak nezne a csempet, es nem tudna, tortent-e valami.
      */}
      {requestNotice && (
        <div
          role="status"
          className="flex items-center gap-3 border-b border-border-soft bg-accent-soft px-4 py-2"
        >
          <p className="min-w-0 flex-1 text-sm text-ink">{requestNotice}</p>
          <button
            type="button"
            onClick={dismissRequestNotice}
            aria-label="Bezárás"
            className="shrink-0 rounded-control px-2 py-1 text-xs text-ink-muted hover:bg-surface-hover hover:text-ink"
          >
            ×
          </button>
        </div>
      )}

      {/* ─── Fo terulet ───────────────────────────────────────────────────── */}
      <div className="relative flex min-h-0 flex-1">
        {/* Lejart idozito: nagy, animalt ora - mindenkinek. */}
        <TimerAlarm huddle={huddle} />
        {incomingRequest && (
          <RequestOverlay
            kind={incomingRequest.kind}
            by={nameOf(incomingRequest.from)}
            onAnswer={answerRequest}
          />
        )}
        {mutedByOther && (
          <MutedByOtherOverlay
            by={nameOf(mutedByOther)}
            onUnmute={toggleMute}
            onDismiss={() => setAckedMutedBy(mutedByOther)}
          />
        )}
        <div className="flex min-h-0 flex-1 flex-col gap-3 p-3">
          {lathato.length > 0 && (
            /*
              OSZTOTT KEPERNYO.

              Ket megosztas gyakran egyutt ertelmes: egy bemutato es a hozza
              tartozo tablazat, vagy ket ember ugyanannak a kodnak a ket fele.
              Kulon-kulon nezve a ketto kozott oda-vissza kapcsolgatas lenne, es
              epp az osszefugges veszne el - amiert egyaltalan nezzuk oket.

              Keskeny ablakban EGYMAS ALA kerulnek: ket felbevagott kepernyo egy
              keskeny sav mellett mar egyiket sem lehetne elolvasni.

              Az `auto-rows-fr` a harmadik-negyedik megosztas miatt kell -
              enelkul a masodik sor a tartalmahoz igazodna, es a ket sor
              kulonbozo magas lenne.
            */
            <div
              className={[
                'grid min-h-0 flex-1 auto-rows-fr gap-3',
                lathato.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2',
              ].join(' ')}
            >
              {lathato.map((share) => (
                // A `ScreenShareView` flex-gyerekkent meretezi magat, ezert kap
                // sajat flex-keretet: a racs csak a helyet osztja ki neki.
                <div key={share.identity} className="flex min-h-0 min-w-0">
                  <ScreenShareView
                    track={share.track}
                    identity={share.identity}
                    label={whoIs(share.identity)}
                    /*
                      A SAJAT megosztast zold keret veszi korul.

                      Ez a valasz arra, hogy megosztas kozben nem latszott, MIT
                      osztok meg valojaban - jo ablakot valasztottam-e.
                    */
                    isMine={share.identity === meId}
                    // A kijelzo-valaszto es a keret-figyelmeztetes CSAK a sajat
                    // kepunkhoz tartozik: masen nincs mit beallitani.
                    frameNote={share.identity === meId ? frameNote : null}
                    monitors={share.identity === meId && shareFrame === 'ask' ? shareMonitors : []}
                    lastMonitor={rememberedMonitor()}
                    onPickMonitor={(name) => void chooseShareMonitor(name)}
                  />
                </div>
              ))}
            </div>
          )}

          {/*
            Gyorsszavazas allasa - felul, amig van szavazat. LEBEG (absolute): ha
            a racsot tolna le, a csempek minden szavazasnal ugrananak.
          */}
          {groupVotes.length > 0 && (
            <QuickVoteTally
              votes={groupVotes}
              nameFor={(id) =>
                id === meId
                  ? 'te'
                  : (huddle.guests.find((guest) => guest.identity === id)?.name ?? nameOf(id))
              }
              onClear={clearQuickVotes}
            />
          )}

          {/* A hivas telefonos fele - csak telefonhivasnal van. */}
          <PhoneLegs huddle={shownHuddle} meId={meId} speakingIds={speakingIds} />

          {/* Vendegek (H2): a varakozok beengedese, a bent levok jogai. */}
          <HuddleGuests
            huddle={shownHuddle}
            speakingIds={speakingIds}
            cameraTracks={cameraTracks}
          />

          <ParticipantGrid
            participants={shownParticipants}
            hostId={huddle.hostId}
            canHandOver={isHost}
            onHandOver={(userId) => void setHuddleHost(huddle.id, userId).catch(() => undefined)}
            meId={meId}
            speakingIds={speakingIds}
            cameraTracks={cameraTracks}
            compact={lathato.length > 0}
            handQueue={handQueue}
            quickVotes={quickVotes}
            awayIds={awayIds}
            onMute={(userId) => void muteOther(userId)}
            onRequest={requestFrom}
            canRequest={!isPaused}
            volumes={participantVolumes}
            onVolume={setParticipantVolume}
            onCancelInvite={(userId) => void cancelInvite(userId)}
            extra={<TimerTile huddle={huddle} meId={meId} compact={lathato.length > 0} />}
          />
        </div>

        {/*
          Az ugyfel-panel a chat MELLETT all: a ketto kulon kerdes, es egy
          telefonhivasnal gyakran mindketto kell (a chatben a kollegakkal
          egyeztetnek, kozben az ugyfel adatait nezik).
        */}
        {phoneNumber && customerOpen && (
          <CustomerPanel
            huddleId={huddle.id}
            number={phoneNumber}
            live={livePhone}
            onClose={() => setCustomerOpen(false)}
          />
        )}
        {scriptOpen && (
          <aside className="flex shrink-0 flex-col max-sm:absolute max-sm:inset-0 max-sm:z-30 max-sm:bg-surface-raised border-l border-border-soft sm:w-[30rem]">
            <TestScriptReader onClose={() => setScriptOpen(false)} />
          </aside>
        )}
        {notesOpen && <HuddleNotes huddleId={huddle.id} onClose={() => setNotesOpen(false)} />}
        {huddle.poll && pollPanelOpen && (
          <HuddlePollPanel
            huddle={huddle}
            poll={huddle.poll}
            meId={meId}
            onClose={() => setPollPanelOpen(false)}
          />
        )}
        {isHost && breakout && breakoutPanelOpen && (
          <BreakoutHostPanel
            huddle={huddle}
            breakout={breakout}
            onClose={() => setBreakoutPanelOpen(false)}
          />
        )}
        {chatOpen && !breakout && <HuddleChat meId={meId} onClose={() => setChatOpen(false)} />}
      </div>

      <FloatingReactions />

      {/* ─── Vezerlosav ───────────────────────────────────────────────────── */}
      {!compact && (
        <footer className="relative flex items-center gap-1.5 border-t border-border-soft bg-surface-sunken px-3 py-2.5">
          {/*
          Szunet alatt a media-gombok ELTUNNEK, nem csak halvanyak.

          Egy latszolag mukodo "nemitas feloldasa" gomb szunetben hazudna: a
          mikrofont a szerver is lezarta, tehat nem kapcsolna vissza. Inkabb
          egyetlen ut marad - vissza a hivasba -, es utana minden a helyen van.
        */}
          {!isPaused && (
            <>
              {/*
              OSSZETARTOZO GOMBOK EGY KERETBEN.

              A nyil es az elmosas onmagaban nem mondta meg, mihez tartozik:
              egy magaban allo nyil a sor kozepen barmit jelenthet. Most a
              mikrofon a sajat eszkoz-nyilaval, a kamera a sajat elmosasaval
              egy kozos keretben all, valasztovonallal - mint egy osztott gomb.
            */}
              <ControlGroup>
                <Control
                  label={isMuted ? 'Némítás feloldása (Ctrl+M)' : 'Némítás (Ctrl+M)'}
                  active={!isMuted}
                  onClick={toggleMute}
                  grouped
                >
                  <Icon name={isMuted ? 'mikrofonNema' : 'mikrofon'} />
                </Control>
                <Control
                  ref={devicesButtonRef}
                  label="Mikrofon, hangszóró, kamera kiválasztása"
                  active={devicesOpen}
                  onClick={() => setDevicesOpen((open) => !open)}
                  grouped
                  narrow
                >
                  <Icon name="le" size="sm" />
                </Control>
              </ControlGroup>

              <ControlGroup>
                <Control
                  label={isCameraOn ? 'Kamera kikapcsolása' : 'Kamera'}
                  active={isCameraOn}
                  onClick={() => void toggleCamera()}
                  disabled={isCameraBusy}
                  grouped
                >
                  <Icon name="kamera" />
                </Control>

                {/*
                A HATTER-valaszto CSAK bekapcsolt kameranal jelenik meg.

                Kamera nelkul nincs mi moge hatteret tenni, es egy olyan gomb,
                ami latszolag semmit nem csinal, rosszabb, mintha ott sem lenne.
                Felirata is van: az ikon egymagaban nem arulta el, mit csinal.
              */}
                {isCameraOn && (
                  <Control
                    ref={backgroundButtonRef}
                    label="Háttér: elmosás vagy saját kép"
                    active={background.kind !== 'none' || backgroundOpen}
                    onClick={() => setBackgroundOpen((open) => !open)}
                    grouped
                  >
                    <span className="flex items-center gap-1.5">
                      <Icon name="rejt" />
                      <span className="hidden text-xs font-medium sm:inline">Háttér</span>
                    </span>
                  </Control>
                )}
              </ControlGroup>

              {/*
              MEGOSZTAS KOZBEN a gomb mellett nyil jelenik meg: a megosztott tartalom
              (masik ablak vagy kijelzo) a megosztas LEALLITASA nelkul cserelheto.
              Az ablakvalasztot a nyil kattintasa nyitja; ha a felhasznalo megsem
              valaszt, a regi megosztas valtozatlanul megy tovabb.
            */}
              {isSharingScreen ? (
                <ControlGroup>
                  <Control
                    label="Megosztás leállítása"
                    active
                    onClick={() => void toggleScreenShare()}
                    grouped
                  >
                    <Icon name="kepernyo" />
                  </Control>
                  <Control
                    label="Másik ablak vagy képernyő megosztása (a megosztás megszakítása nélkül)"
                    onClick={() => void switchScreenShare()}
                    grouped
                    narrow
                  >
                    <Icon name="le" size="sm" />
                  </Control>
                </ControlGroup>
              ) : (
                <Control
                  label="Képernyőmegosztás"
                  active={false}
                  onClick={() => void toggleScreenShare()}
                >
                  <Icon name="kepernyo" />
                </Control>
              )}

              {/*
              GYORSSZAVAZAS - "aki egyetert, nyomja meg a pipat".

              Nincs letrehozasa: a gombok mindig itt vannak, a kerdes szoban
              hangzik el. Az allas felul latszik, a csempeken pedig, ki mire
              nyomott.
            */}
              <ControlGroup>
                <QuickVoteButton vote="yes" active={myVote === 'yes'} onClick={castQuickVote} />
                <QuickVoteButton vote="no" active={myVote === 'no'} onClick={castQuickVote} />
              </ControlGroup>

              <Control
                ref={reactionsButtonRef}
                label="Reakció"
                active={reactionsOpen}
                onClick={() => setReactionsOpen((o) => !o)}
              >
                <Icon name="emoji" />
              </Control>

              {/*
              MINDJART JOVOK - kiugrok egy percre.

              A tobbiek a csempemen latjak, hogy nem vagyok a gepnel - nem kell
              hiaba szolitaniuk. Tavozaskor a mikrofon elnemul; visszaterni
              ezzel a gombbal vagy a mikrofon bekapcsolasaval lehet.
            */}
              <Control
                label={
                  isAway
                    ? 'Visszajöttem'
                    : 'Mindjárt jövök — a többiek látják, hogy nem vagy a gépnél'
                }
                active={isAway}
                onClick={toggleAway}
              >
                <Icon name="kave" />
              </Control>

              {/*
              JELENTKEZES - "szolni szeretnek".

              Nagyobb hivasban nem lehet csak ugy kozbevagni: a kez jelzi, es
              egy apro pitty szol mindenkinel. A gyorsgomb (Ctrl+H) azert kell,
              mert jelentkezni altalaban beszed kozben akar az ember, amikor
              nem gombot keresgel.
            */}
              <Control
                label={isHandRaised ? 'Kéz leengedése (Ctrl+H)' : 'Jelentkezés (Ctrl+H)'}
                active={isHandRaised}
                onClick={toggleHand}
              >
                <Icon name="kez" />
              </Control>
            </>
          )}

          {/*
          Az atiratozas kapcsoloja.

          Barmelyik resztvevo atallithatja, es MINDENKI latja az eredmenyt -
          ugyanaz a szabaly, mint a jelzesnel: felvetelrol nem lehet titokban
          donteni. Csatornaban alapbol be, privat beszelgetesben alapbol ki.
        */}
          <Control
            label={isTranscribing ? 'Átiratozás kikapcsolása' : 'Átirat készítése a hívásról'}
            active={isTranscribing}
            onClick={() => void toggleTranscribe()}
          >
            <Icon name="atirat" />
          </Control>

          {/*
          SZUNET: bent maradok, de eppen nem vagyok itt.

          Nem ugyanaz, mint a nemitas: a nemitott hallgat minket, a
          szuneteltetett nem hall es nem hallatszik - es kozben belephet egy
          masik olelesbe. Ez a valasz arra a napra, amiben tobb megbeszeles van.
        */}
          <Control
            label={isPaused ? 'Vissza a hívásba' : 'Szünet — bent maradsz, de elnémulsz'}
            active={isPaused}
            onClick={() => void togglePause()}
          >
            <Icon name={isPaused ? 'folytatas' : 'szunet'} />
          </Control>

          {backgroundOpen && isCameraOn && (
            <BackgroundPicker
              value={background}
              onChange={(next) => void setBackground(next)}
              triggerRef={backgroundButtonRef}
              onClose={() => setBackgroundOpen(false)}
            />
          )}

          {reactionsOpen && (
            <div
              ref={reactionsRef}
              className="absolute bottom-full left-3 mb-2 flex gap-1 rounded-card border border-border bg-surface-raised p-1.5 shadow-modal"
            >
              {huddleReactions.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => {
                    sendReaction(emoji);
                    setReactionsOpen(false);
                  }}
                  className="rounded-control px-2 py-1 text-lg transition-transform hover:scale-125"
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          {/*
          KULON mikrofon es hangszoro.

          A ketto gyakran NEM ugyanaz az eszkoz: fejhallgatoval veszek fel, de
          a hangfalon hallgatom - vagy forditva. Amig egyetlen lista volt, ez a
          parositas egyszeruen nem volt beallithato.
        */}
          {devicesOpen && (
            <div
              ref={devicesRef}
              className="absolute bottom-full left-3 mb-2 w-72 rounded-card border border-border bg-surface-raised p-1.5 shadow-modal"
            >
              <DeviceGroup
                title="Mikrofon"
                icon="mikrofon"
                devices={devices}
                activeId={activeDeviceId}
                onPick={(id) => {
                  void switchDevice(id);
                  setDevicesOpen(false);
                }}
              />
              <NoiseChoice />

              {/*
              KAMERA.

              Beepitett + kulso webkamera a leggyakoribb parositas; a ketto
              kozott a rendszer beallitasaiban valtani hivas kozben nehezkes.
            */}
              {videoDevices.length > 0 ? (
                <DeviceGroup
                  title="Kamera"
                  icon="kamera"
                  devices={videoDevices}
                  activeId={activeCameraId}
                  onPick={(id) => {
                    void switchCamera(id);
                    setDevicesOpen(false);
                  }}
                />
              ) : (
                /*
                Ures lista: ennek OKA van, es meg is mondjuk.

                A kamerak nevet a bongeszo csak KAMERA-engedely mellett arulja
                el - a mikrofone nem eleg hozza. Amig a kamera egyszer sem
                volt bekapcsolva, itt nincs mit felkinalni.
              */
                <div className="mt-1.5 border-t border-border-soft pt-1.5">
                  <p className="flex items-center gap-1.5 px-2 py-1 text-2xs font-semibold uppercase tracking-wide text-ink-faint">
                    <Icon name="kamera" size="sm" /> Kamera
                  </p>
                  <p className="px-2 pb-1 pt-0.5 text-2xs text-ink-faint">
                    A kamerák listájához kapcsold be egyszer a kamerát — a nevüket a böngésző csak
                    azután árulja el.
                  </p>
                </div>
              )}

              {canChooseOutput ? (
                <>
                  <DeviceGroup
                    title="Hangszóró"
                    icon="huddle"
                    devices={outputDevices}
                    activeId={activeOutputId}
                    onPick={(id) => {
                      void switchOutput(id);
                      setDevicesOpen(false);
                    }}
                  />
                  {/*
                  Amig nincs valasztas, a bongeszo dont - es ezt KIMONDJUK.

                  Ha egy sort magatol megjelolnenk "aktivkent", az hazugsag
                  lenne: a lejatszas addig nincs eszkozhoz kotve. Ennel
                  rosszabb volt a korabbi valtozat, ami a rendszer
                  alapertelmezesere is ALLITOTTA - ettol a fejhallgatobol a
                  gep hangszorojara vandorolt a hang.
                */}
                  {outputDevices.length === 0 ? (
                    /*
                    Ures lista: ennek OKA van, es meg is mondjuk.

                    A bongeszo csak mikrofon-engedely mellett arulja el a
                    hangszorok nevet es azonositojat. Enelkul egyetlen, nevtelen
                    bejegyzest ad, amit szandekosan kiszurunk - egy ilyen sorra
                    kattintva ugyanis a rendszer alapertelmezesere (tipikusan a
                    gep hangszorojara) kapcsolna a hang.
                  */
                    <p className="px-2 pb-1 pt-0.5 text-2xs text-ink-faint">
                      A hangszórók listájához a böngészőnek tartósan engedélyeznie kell a mikrofont
                      ezen az oldalon.
                    </p>
                  ) : (
                    <p className="px-2 pb-1 pt-0.5 text-2xs text-ink-faint">
                      {activeOutputId === null
                        ? 'Most a böngésző alapértelmezése szól.'
                        : /*
                          A WINDOWS FELULIRHATJA - es ez tenyleg elofordult.
                          Ott a bongeszohoz rogzitett kimeneti eszkozt egyetlen
                          weboldal sem tudja felulirni; enelkul a felhasznalo a
                          sajat fejhallgatojaban keresne a hibat.
                        */
                          'Ha a hang mégsem vált át: a Windows hangerőkeverőjében a böngészőhöz rögzített eszköz felülírja ezt.'}
                    </p>
                  )}
                </>
              ) : (
                /*
                Ahol a bongeszo nem tud kimenetet valtani (pl. Safari), ott nem
                mutatunk latszolag mukodo valasztot - inkabb megmondjuk, hol
                lehet allitani.
              */
                <p className="mt-1.5 border-t border-border-soft px-2 pb-1 pt-2 text-2xs text-ink-faint">
                  Ez a böngésző nem tud hangszórót váltani — a rendszer hangbeállításaiban tudod
                  átállítani.
                </p>
              )}
            </div>
          )}

          {/*
          A kilepes a legnagyobb es a legkonnyebben eltalalhato gomb. Ez nem
          esztetikai dontes: a projekt egyik fo indoka az volt, hogy a
          Slackben nem lehetett kilepni a huddle-bol.
        */}
          <button
            type="button"
            onClick={leave}
            title="Kilépés (vagy kétszer Esc)"
            className="ml-auto rounded-control bg-[var(--danger)] px-4 py-2 text-sm font-semibold text-white transition-transform duration-(--duration-quick) hover:brightness-110 active:scale-95"
          >
            Kilépés
          </button>
        </footer>
      )}

      {/* ─── Vezerlosav (telefon) ─────────────────────────────────────────── */}
      {compact && (
        <footer className="relative flex items-center justify-between gap-1 border-t border-border-soft bg-surface-sunken px-3 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-2">
          {isPaused ? (
            <button
              type="button"
              onClick={() => void togglePause()}
              className="min-h-11 flex-1 rounded-control bg-accent px-3 py-2 text-sm font-semibold text-accent-ink"
            >
              Vissza a hívásba
            </button>
          ) : (
            <>
              <Control
                label={isMuted ? 'Némítás feloldása' : 'Némítás'}
                active={!isMuted}
                onClick={toggleMute}
              >
                <Icon name={isMuted ? 'mikrofonNema' : 'mikrofon'} />
              </Control>
              <Control
                label={isCameraOn ? 'Kamera kikapcsolása' : 'Kamera'}
                active={isCameraOn}
                onClick={() => void toggleCamera()}
                disabled={isCameraBusy}
              >
                <Icon name="kamera" />
              </Control>
              {typeof navigator.mediaDevices?.getDisplayMedia === 'function' && (
                <Control
                  label={isSharingScreen ? 'Megosztás leállítása' : 'Képernyőmegosztás'}
                  active={isSharingScreen}
                  onClick={() => void toggleScreenShare()}
                >
                  <Icon name="kepernyo" />
                </Control>
              )}
              <span className="relative">
                <Control
                  label={breakout ? 'Chat (a kiscsoportos időre zárolva)' : 'Chat'}
                  active={chatOpen && !breakout}
                  disabled={Boolean(breakout)}
                  onClick={() => (chatOpen ? setChatOpen(false) : openChat())}
                >
                  <Icon name="chat" />
                </Control>
                {hasUnreadChat && !breakout && (
                  <span
                    role="img"
                    aria-label="új üzenet"
                    className="absolute right-1 top-1 size-2.5 rounded-full border-2 border-surface-sunken bg-[var(--danger)]"
                  />
                )}
              </span>
              <span className="relative">
                <Control label="Több" active={moreOpen} onClick={() => setMoreOpen(true)}>
                  <Icon name="szerkesztesTobb" />
                </Control>
                {Boolean(huddle.poll) && !pollPanelOpen && (
                  <span
                    role="img"
                    aria-label="nyitott szavazás"
                    className="absolute right-1 top-1 size-2.5 rounded-full border-2 border-surface-sunken bg-[var(--danger)]"
                  />
                )}
              </span>
            </>
          )}

          {/* A kilepes mindig ott van, es a legnagyobb - ez a projekt egyik fo indoka. */}
          <button
            type="button"
            onClick={leave}
            aria-label="Kilépés"
            title="Kilépés (vagy kétszer Esc)"
            className="grid size-11 shrink-0 place-items-center rounded-control bg-[var(--danger)] text-lg text-white transition-transform active:scale-95"
          >
            <Icon name="kilepes" />
          </button>

          {backgroundOpen && isCameraOn && (
            <BackgroundPicker
              value={background}
              onChange={(next) => void setBackground(next)}
              triggerRef={backgroundButtonRef}
              onClose={() => setBackgroundOpen(false)}
            />
          )}
        </footer>
      )}

      {compact && (
        <HuddleMoreSheet
          open={moreOpen}
          onOpenChange={setMoreOpen}
          reactions={huddleReactions}
          onReact={sendReaction}
          tiles={moreTiles}
        >
          {screenShares.length > 0 && (
            <MoreSection title="Megosztások">
              <div className="flex flex-wrap gap-1.5">
                {screenShares.map((share) => {
                  const on = visibleShares.includes(share.identity);
                  return (
                    <button
                      key={share.identity}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleShare(share.identity)}
                      className={`rounded-pill border px-3 py-1.5 text-xs font-medium ${
                        on
                          ? 'border-accent bg-accent-soft text-accent'
                          : 'border-border-soft text-ink-muted'
                      }`}
                    >
                      <Icon name="kepernyo" />{' '}
                      {share.identity === meId ? 'Te osztod meg' : whoIs(share.identity)}
                    </button>
                  );
                })}
              </div>
            </MoreSection>
          )}

          {(huddle.timer || huddle.hostId === meId || huddle.timerOpen) && (
            <MoreSection title="Stopper / időzítő">
              <StopwatchControl huddle={huddle} meId={meId} inline />
            </MoreSection>
          )}

          {showDialer && (
            <MoreSection title="Tárcsázó">
              <PhoneDialer huddleId={huddle.id} channelId={huddle.channelId} />
            </MoreSection>
          )}

          <MoreSection title="Hang és eszközök">
            <SpeakerChoice
              outputDevices={outputDevices}
              activeOutputId={activeOutputId}
              canChooseOutput={canChooseOutput}
              onPick={(id) => void switchOutput(id)}
            />
            <details className="mt-2 rounded-control border border-border-soft">
              <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-ink-muted">
                Mikrofon, kamera, hangszóró választása
              </summary>
              <div className="px-1.5 pb-1.5">
                <DeviceGroup
                  title="Mikrofon"
                  icon="mikrofon"
                  devices={devices}
                  activeId={activeDeviceId}
                  onPick={(id) => void switchDevice(id)}
                />
                <NoiseChoice />
                <DeviceGroup
                  title="Kamera"
                  icon="kamera"
                  devices={videoDevices}
                  activeId={activeCameraId}
                  onPick={(id) => void switchCamera(id)}
                />
                {canChooseOutput && (
                  <DeviceGroup
                    title="Hangszóró"
                    icon="huddle"
                    devices={outputDevices}
                    activeId={activeOutputId}
                    onPick={(id) => void switchOutput(id)}
                  />
                )}
              </div>
            </details>
          </MoreSection>
        </HuddleMoreSheet>
      )}
    </div>
  );
}

// ─── Resztvevok ───────────────────────────────────────────────────────────────

function ParticipantGrid({
  participants,
  hostId,
  canHandOver,
  onHandOver,
  meId,
  speakingIds,
  cameraTracks,
  compact,
  handQueue,
  quickVotes,
  awayIds,
  onMute,
  onRequest,
  canRequest,
  volumes,
  onVolume,
  onCancelInvite,
  extra,
}: {
  participants: HuddleParticipant[];
  /** A meg fel nem vett behivas visszavonasa (a hivjuk csempe gombja). */
  onCancelInvite: (userId: string) => void;
  /** A resztvevok MELLE kerulo blokk (a stopper/idozito nagy csempeje). */
  extra?: React.ReactNode;
  /** A hazigazda azonositoja - a csempen jelolve. */
  hostId: string | null;
  /** Atadhatom-e a hazigazda-szerepet (csak a hazigazda). */
  canHandOver: boolean;
  onHandOver: (userId: string) => void;
  meId: string;
  speakingIds: string[];
  cameraTracks: { identity: string; track: Track }[];
  compact: boolean;
  /** A jelentkezok azonositoi, a jelentkezes sorrendjeben. */
  handQueue: string[];
  quickVotes: Record<string, QuickVote>;
  /** Akik "mindjart jonnek": azonosito -> a tavozas idopontja. */
  awayIds: Record<string, number>;
  onMute: (userId: string) => void;
  onRequest: (userId: string, kind: HuddleRequestKind) => void;
  /** Szuneten nem kerhetunk: nem latnank, mit valaszolnak. */
  canRequest: boolean;
  /** Kit mennyire halkan hallunk - CSAK nalunk. Hianyzo ertek = valtozatlan. */
  volumes: Record<string, number>;
  onVolume: (userId: string, volume: number) => void;
}) {
  return (
    <ul
      className={
        compact
          ? 'flex shrink-0 gap-2 overflow-x-auto pb-1'
          : /**
             * A sorok magassaga korlatos, a racs pedig fuggolegesen kozepre
             * all. Enelkul egyetlen resztvevo csempeje az egesz ablakot
             * kitoltene - egy oriasi negyzet egy monogrammal.
             */
            'grid flex-1 content-center gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
      }
    >
      {participants.map((participant) => (
        <ParticipantTile
          key={participant.user.id}
          participant={participant}
          isHost={participant.user.id === hostId}
          canHandOver={canHandOver}
          onHandOver={() => onHandOver(participant.user.id)}
          isMe={participant.user.id === meId}
          isSpeaking={speakingIds.includes(participant.user.id)}
          camera={cameraTracks.find((item) => item.identity === participant.user.id)?.track ?? null}
          compact={compact}
          handPosition={handQueue.indexOf(participant.user.id) + 1}
          quickVote={quickVotes[participant.user.id] ?? null}
          away={Boolean(awayIds[participant.user.id])}
          handCount={handQueue.length}
          onMute={() => onMute(participant.user.id)}
          onRequest={(kind) => onRequest(participant.user.id, kind)}
          canRequest={canRequest}
          volume={volumes[participant.user.id] ?? 1}
          onVolume={(value) => onVolume(participant.user.id, value)}
          onCancelInvite={() => onCancelInvite(participant.user.id)}
        />
      ))}
      {extra}
    </ul>
  );
}

function ParticipantTile({
  participant,
  isHost,
  canHandOver,
  onHandOver,
  isMe,
  isSpeaking,
  camera,
  compact,
  handPosition,
  quickVote,
  handCount,
  away,
  onMute,
  onRequest,
  canRequest,
  volume,
  onVolume,
  onCancelInvite,
}: {
  participant: HuddleParticipant;
  /** O a hazigazda. */
  isHost: boolean;
  canHandOver: boolean;
  onHandOver: () => void;
  isMe: boolean;
  isSpeaking: boolean;
  camera: Track | null;
  compact: boolean;
  /** Hanyadikkent jelentkezett (1-tol); 0 = nem jelentkezik. */
  handPosition: number;
  /** Gyorsszavazat: kis pipa / X a csempe jobb felso sarkaban. */
  quickVote: QuickVote | null;
  handCount: number;
  /** "Mindjart jovok" - bent van, de nincs a gepnel. */
  away: boolean;
  onMute: () => void;
  onRequest: (kind: HuddleRequestKind) => void;
  canRequest: boolean;
  volume: number;
  onVolume: (volume: number) => void;
  /** A meg fel nem vett behivas visszavonasa. */
  onCancelInvite: () => void;
}) {
  const { nameOf } = useUsers();
  const videoRef = useRef<HTMLDivElement | null>(null);
  const invited = participant.state === 'invited';
  const paused = participant.isPaused && !invited;
  /*
    A kamera kikapcsolasa a LiveKitben NEMITAS: a sav megmarad. Nemitott savbol
    fekete kep lenne, ezert akkor az avatar latszik. A `cameraLive` a hatas
    fuggosege is: bekapcsolaskor a keret ujra letrejon, es a sav ujra rakotodik.
  */
  const cameraLive = camera !== null && !camera.isMuted;

  useEffect(() => {
    if (!camera || !cameraLive) return;
    const element = camera.attach();
    /**
     * A SAJAT kepunket tukrozve mutatjuk.
     *
     * Aki magat latja, azt varja, hogy tukorkepet lasson - a nem tukrozott
     * onkep zavaro. A tobbiek kepet viszont nem forgatjuk meg.
     */
    element.className = `h-full w-full object-cover${isMe ? ' -scale-x-100' : ''}`;
    videoRef.current?.replaceChildren(element);
    return () => element.remove();
  }, [camera, cameraLive, isMe]);

  const name = isMe ? 'Te' : nameOf(participant.user);

  return (
    <li
      title={
        invited
          ? `${nameOf(participant.user)} — hívjuk`
          : paused
            ? `${nameOf(participant.user)} — szüneten`
            : away
              ? `${nameOf(participant.user)} — mindjárt jön`
              : nameOf(participant.user)
      }
      className={compact ? 'shrink-0' : 'flex flex-col gap-1.5'}
    >
      {/*
        A KEP a kartya - kitolti, nem ul benne.
        A huddle nem lista: az arcot nezzuk, nem a nevet. A nev ezert a kartya
        ALA kerult, ahol nem veszi el a helyet a kaptol, es kamerakepnel sem
        kell a videora irni.
      */}
      <TileMenu
        name={nameOf(participant.user)}
        enabled={!isMe && !invited}
        canShare={canRequest && !paused && !participant.isSharingScreen}
        canUnmute={canRequest && !paused && participant.isMuted}
        canMute={!paused && !participant.isMuted}
        onRequest={onRequest}
        onMute={onMute}
        canHandOver={canHandOver && !paused}
        onHandOver={onHandOver}
      >
        <div
          className={[
            'group relative overflow-hidden rounded-card bg-surface-sunken',
            'transition-[opacity,box-shadow,filter] duration-(--duration-base)',
            compact ? 'size-14' : 'aspect-[4/3] w-full',
            /**
             * A behivott LATHATOAN maskent nez ki: halvany, szaggatott kerettel.
             * Ez a vizualis visszaigazolas a csatlakozasrol - a hivo latja, hogy
             * elment a hivas, es azt is, amikor a masik tenyleg belep.
             */
            invited ? 'border-2 border-dashed border-border-strong opacity-60' : '',
            /*
            A szuneteltetett HALVANY - de nem szaggatott, mint a behivott: o
            bent van, csak eppen nincs jelen. A ketto kulonbozo allapot, es a
            kulonbsegnek latszania kell.
          */
            paused ? 'opacity-50 grayscale' : '',
            isSpeaking && !invited && handPosition === 0 ? 'ring-2 ring-accent' : '',
            /*
            A jelentkezo csempeje MELEG, vastag keretet kap - a hivas akcentus-
            szinetol elutot, hogy ne keveredjen a "beszel" jelzessel.
          */
            handPosition > 0 ? 'ring-4 ring-[oklch(84%_0.17_85)]' : '',
          ].join(' ')}
        >
          {cameraLive && !invited ? (
            <div ref={videoRef} className="absolute inset-0 bg-black" />
          ) : (
            <Avatar
              userId={participant.user.id}
              name={participant.user.displayName}
              avatarUrl={participant.user.avatarUrl}
              size={compact ? 'fillSm' : 'fill'}
              className="absolute inset-0"
            />
          )}

          {/*
          MINDJART JON - a kep FOLOTT, elsotetitve.

          Nem egy apro sarok-ikon: a lenyeg, hogy egy pillantasbol latsszon,
          hogy hiaba szolitanank - kamerakepnel is, ahol a szek ures.
        */}
          {away && !invited && (
            <span
              role="img"
              aria-label="mindjárt jön"
              className="absolute inset-0 grid place-items-center bg-black/55 text-white"
            >
              <span
                className={`flex flex-col items-center gap-1 ${compact ? 'text-lg' : 'text-4xl'}`}
              >
                <Icon name="kave" />
                {!compact && <span className="text-xs font-medium">Mindjárt jön</span>}
              </span>
            </span>
          )}

          {/*
          FELEMELT KEZ - a bal felso sarokban, jol lathatoan.

          Tobb jelentkezonel a SORSZAM is ott all: igy mindenki ugyanazt a
          sorrendet latja, es nem kell vitatkozni, ki jon.
        */}
          {quickVote && (
            <span
              role="img"
              aria-label={quickVote === 'yes' ? 'egyetért' : 'nem ért egyet'}
              title={quickVote === 'yes' ? 'Egyetért' : 'Nem ért egyet'}
              className={[
                'absolute z-10 grid place-items-center rounded-full text-white shadow-(--shadow-lift)',
                quickVote === 'yes' ? 'bg-[var(--success)]' : 'bg-[var(--danger)]',
                compact ? 'right-0.5 top-0.5 size-6 text-xs' : 'right-3 top-3 size-12 text-2xl',
              ].join(' ')}
            >
              <Icon name={quickVote === 'yes' ? 'pipa' : 'bezaras'} />
            </span>
          )}
          {handPosition > 0 && (
            <span
              role="img"
              aria-label={`jelentkezik${handCount > 1 ? ` (${handPosition}.)` : ''}`}
              title={`${isMe ? 'Jelentkezel' : 'Jelentkezik'}${handCount > 1 ? ` — ${handPosition}. a sorban` : ''}`}
              className={[
                'absolute z-10 flex animate-hand items-center rounded-pill font-bold',
                // Sarga - minden temaban kiabal, es sotet betuvel olvashato.
                'bg-[oklch(84%_0.17_85)] text-[oklch(28%_0.06_60)] shadow-(--shadow-lift)',
                compact
                  ? 'left-0.5 top-0.5 gap-0.5 px-1.5 py-1 text-sm'
                  : 'left-3 top-3 gap-2 px-4 py-3 text-4xl',
              ].join(' ')}
            >
              <Icon name="kez" />
              {handCount > 1 && (
                <span className={`tabular-nums ${compact ? 'text-xs' : 'text-2xl'}`}>
                  {handPosition}
                </span>
              )}
            </span>
          )}

          {/*
          AZ ALSO SOR: allapot-jelzesek + hangero-csuszka EGY sorban.

          Korabban ket kulon, egymasra rakott reteg volt ugyanabban a sarokban,
          es hoverre a csuszka a nemitas-jelzes ALA csuszott: a ket ikon
          osszemosodott, a csuszka eleje pedig nem latszott. Most a jelzesek
          allnak elol, a csuszka a maradek helyet kapja mellettuk.
        */}
          <div className="absolute inset-x-1 bottom-1 flex items-center gap-1">
            {paused && (
              <span
                role="img"
                aria-label="szüneten"
                className="shrink-0 rounded bg-black/50 px-1 py-1 text-2xs text-white"
              >
                <Icon name="szunet" />
              </span>
            )}
            {participant.isMuted && !invited && !paused && (
              <span
                role="img"
                aria-label="némítva"
                className="shrink-0 rounded bg-black/50 px-1 py-1 text-2xs text-white"
              >
                <Icon name="mikrofonNema" />
              </span>
            )}
            {participant.isSharingScreen && (
              <span
                role="img"
                aria-label="képernyőt oszt meg"
                className="shrink-0 rounded bg-black/50 px-1 py-1 text-2xs text-white"
              >
                <Icon name="kepernyo" />
              </span>
            )}

            {/*
          HANGERO - csak a sajat fulunkre.

          Kulon dolog az "Elnémít"-tol, es ez a kulonbseg latszik is: az a
          szerverre megy es MINDENKINEL elnemiti az illetot, ez viszont csak
          nalunk halkit. A hivasban nem mindenki egyforma hangos - az egyik
          fejhallgatoba beszel kozelrol, a masik a szoba masik vegebol -, es
          eddig erre egyetlen valasz volt, a mindenkire kiterjedo elnemitas.

          A csuszka csak hoverre (es fokuszra) latszik: allandoan kint egy sav
          minden csempen a beszelgetes helyett magat mutatna.
        */}
            {!isMe && !invited && (
              <div
                className={[
                  'flex min-w-0 flex-1 items-center rounded bg-black/60 py-1',
                  compact ? 'gap-1 px-1' : 'gap-1.5 px-1.5',
                  'opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100',
                ].join(' ')}
              >
                {/*
                KIS csempen (megosztas kozben) csak a csuszka marad: 56 keppont
                szelessegben az ikon es a szazalek mellett a csuszkanak mar nem
                jutna hely. A pontos erteket a buboreksugo mondja meg.
              */}
                <span
                  aria-hidden="true"
                  className={`shrink-0 text-2xs text-white/70${compact ? ' hidden' : ''}`}
                >
                  <Icon name="huddle" size="sm" className={volume === 0 ? 'opacity-40' : ''} />
                </span>
                <input
                  type="range"
                  min={0}
                  max={MAX_VOLUME * 100}
                  step={5}
                  value={Math.round(volume * 100)}
                  onChange={(event) => onVolume(Number(event.target.value) / 100)}
                  aria-label={`${nameOf(participant.user)} hangereje — csak nálad`}
                  title={`${nameOf(participant.user)} hangereje — csak nálad (${Math.round(volume * 100)}%)`}
                  className="h-1 min-w-0 flex-1 cursor-pointer accent-[var(--accent)]"
                />
                {/*
                A szazalek csak akkor latszik, ha ELTER az alaptol.

                Valtozatlan hangeronel a "100%" felirat semmit nem mond, viszont
                minden csempen ott ulne. Amint valaki hozzanyul, a szam lesz a
                visszajelzes arrol, hogy tenyleg beallitott valamit.
              */}
                {volume !== 1 && !compact && (
                  <button
                    type="button"
                    onClick={() => onVolume(1)}
                    title="Vissza az alap hangerőre"
                    className="shrink-0 rounded px-1 text-2xs tabular-nums text-white/80 hover:bg-white/20 hover:text-white"
                  >
                    {Math.round(volume * 100)}%
                  </button>
                )}
              </div>
            )}
          </div>

          {invited && !isMe && compact && (
            <button
              type="button"
              onClick={onCancelInvite}
              title="Behívás megszakítása"
              aria-label={` behívásának megszakítása`}
              className="absolute right-0.5 top-0.5 grid size-5 place-items-center rounded-full bg-black/60 text-white hover:bg-[var(--danger)]"
            >
              <Icon name="bezaras" size="sm" />
            </button>
          )}

          {participant.mutedBy && !invited && (
            <span className="absolute right-1 top-1 rounded bg-black/50 px-1 text-2xs text-white/80">
              elnémítva
            </span>
          )}
        </div>
      </TileMenu>

      {!compact && (
        <p className="truncate px-0.5 text-center text-sm font-medium">
          {name}
          {isHost && <span className="ml-1 text-ink-faint">· házigazda</span>}
          {invited && <span className="ml-1 text-ink-faint">· hívjuk</span>}
          {paused && <span className="ml-1 text-ink-faint">· szüneten</span>}
          {away && !invited && <span className="ml-1 text-ink-faint">· mindjárt jön</span>}
        </p>
      )}

      {/*
        A BEHIVAS MEGSZAKITASA.

        Aki rossz embert hivott, vagy meggondolta magat, eddig csak varhatott, amig a hivas
        le nem jar - a masik oldalon addig csorgott. A gomb a csempe ALATT all, nem rajta: a
        behivott csempe halvany, es egy halvany gombot nem venne eszre senki.
      */}
      {invited && !isMe && !compact && (
        <button
          type="button"
          onClick={onCancelInvite}
          className="mx-auto flex items-center gap-1.5 rounded-control border border-border-soft px-2.5 py-1 text-xs font-medium text-ink-muted transition-colors hover:border-[var(--danger)] hover:text-[var(--danger)]"
        >
          <Icon name="bezaras" size="sm" /> Megszakítás
        </button>
      )}
    </li>
  );
}

// ─── Resztvevo-menu ───────────────────────────────────────────────────────────

/**
 * Jobb klikk (telefonon hosszu nyomas) egy resztvevo csempejen.
 *
 * Ami itt van, az MASRA hat: kerek tole valamit, vagy mindenkinel elnemitom.
 * Ezert nem hover-gomb - egy veletlen rakattintas egy hivas kozepen nem
 * nemithat el senkit. A hangero-csuszka marad hoveren: az csak nalam halkit,
 * es azt gyorsan, kereses nelkul kell elerni.
 *
 * A Radix ContextMenu az erintest is kezeli (hosszu nyomas), es billentyuzetrol
 * a menubillentyu / Shift+F10 nyitja - ehhez fokuszalhato a csempe.
 */
function TileMenu({
  name,
  enabled,
  canShare,
  canUnmute,
  canMute,
  onRequest,
  onMute,
  canHandOver,
  onHandOver,
  children,
}: {
  name: string;
  enabled: boolean;
  canShare: boolean;
  canUnmute: boolean;
  canMute: boolean;
  onRequest: (kind: HuddleRequestKind) => void;
  onMute: () => void;
  /** A hazigazda atadhatja a szerepet ennek a kollegának. */
  canHandOver: boolean;
  onHandOver: () => void;
  children: React.ReactNode;
}) {
  if (!enabled) return <>{children}</>;

  const item =
    'flex cursor-pointer select-none items-center gap-2 rounded-control px-2.5 py-1.5 text-sm outline-none data-[disabled]:cursor-default data-[disabled]:opacity-40 data-[highlighted]:bg-surface-hover';

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>
        <div
          tabIndex={0}
          aria-label={`${name} — jobb klikk vagy hosszú nyomás a műveletekhez`}
          className="rounded-card outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {children}
        </div>
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className="animate-fade-rise z-50 min-w-60 rounded-card border border-border-soft bg-surface-raised p-1 shadow-(--shadow-modal)">
          <ContextMenu.Label className="truncate px-2.5 pb-1 pt-1.5 text-2xs uppercase tracking-wide text-ink-faint">
            {name}
          </ContextMenu.Label>
          <ContextMenu.Item
            className={item}
            disabled={!canShare}
            onSelect={() => onRequest('screen')}
          >
            <Icon name="kepernyo" /> Képernyőmegosztás kérése
          </ContextMenu.Item>
          <ContextMenu.Item
            className={item}
            disabled={!canUnmute}
            onSelect={() => onRequest('unmute')}
          >
            <Icon name="mikrofon" /> Némítás feloldásának kérése
          </ContextMenu.Item>
          <ContextMenu.Separator className="my-1 h-px bg-border-soft" />
          {/*
            A VISSZAKAPCSOLAST csak o vegezheti - a sajat mikrofonjahoz mindenki
            maga fer hozza. Ezert van feloldas-KERES, es nincs feloldas.
          */}
          <ContextMenu.Item
            className={`${item} text-[var(--danger)]`}
            disabled={!canMute}
            onSelect={onMute}
          >
            <Icon name="mikrofonNema" /> Elnémítás mindenkinél
          </ContextMenu.Item>
          {canHandOver && (
            <>
              <ContextMenu.Separator className="my-1 h-px bg-border-soft" />
              <ContextMenu.Item className={item} onSelect={onHandOver}>
                <Icon name="tagok" /> Házigazda-szerep átadása
              </ContextMenu.Item>
            </>
          )}
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}

/**
 * Egy kolléga kér tőlünk valamit - Igen vagy Nem.
 *
 * Ugyanaz a forma, mint a "Lenemitottak" jelzes: kozepen, jol lathatoan. Egy
 * sarokba bujtatott kerdes mellett el lehetne beszelni harminc masodpercig.
 */
function RequestOverlay({
  kind,
  by,
  onAnswer,
}: {
  kind: HuddleRequestKind;
  by: string;
  onAnswer: (accept: boolean) => void;
}) {
  const screen = kind === 'screen';

  return (
    <div className="pointer-events-none absolute inset-0 z-30 grid place-items-center bg-black/35 p-4">
      <div
        role="alertdialog"
        aria-labelledby="huddle-request-title"
        aria-describedby="huddle-request-text"
        onKeyDown={(event) => {
          if (event.key === 'Escape') onAnswer(false);
        }}
        className="pointer-events-auto flex w-full max-w-xs animate-fade-rise flex-col items-center gap-3 rounded-card border-2 border-accent bg-surface-raised px-6 py-5 text-center shadow-(--shadow-modal)"
      >
        <span
          aria-hidden
          className="grid size-20 place-items-center rounded-full bg-accent text-4xl text-accent-ink shadow-(--shadow-lift)"
        >
          <Icon name={screen ? 'kepernyo' : 'mikrofon'} />
        </span>
        <div>
          <p id="huddle-request-title" className="font-display text-lg font-semibold text-ink">
            {screen ? 'Képernyőmegosztás kérése' : 'Mikrofon visszakapcsolása'}
          </p>
          <p id="huddle-request-text" className="text-sm text-ink-muted">
            <span className="font-medium text-ink">{by}</span>{' '}
            {screen
              ? 'szeretné, ha megosztanád a képernyődet. Te választod ki, mit osztasz meg.'
              : 'szeretné, ha visszakapcsolnád a mikrofonodat.'}
          </p>
        </div>
        <div className="flex w-full gap-2">
          <button
            type="button"
            onClick={() => onAnswer(false)}
            className="flex-1 rounded-control bg-surface-sunken px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-surface-hover"
          >
            Nem
          </button>
          <button
            type="button"
            autoFocus
            onClick={() => onAnswer(true)}
            className="flex-1 rounded-control bg-accent px-4 py-2.5 text-sm font-semibold text-accent-ink transition-opacity hover:opacity-90"
          >
            Igen
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Teszthivas ───────────────────────────────────────────────────────────────

/**
 * TESZTHIVAS jelzese - mindenkinek, ha be van kapcsolva.
 *
 * Ilyenkor a hivas hangja 30 napig megmarad (az atirat-meres miatt). Ugyanaz a
 * szabaly, mint az atiratnal: aki bent ul, annak tudnia kell rola. Kapcsolni
 * csak szuperadmin tudja - neki a gomb kikapcsolt allapotban is latszik.
 *
 * A jelolest a hivas ELEJEN kell feltenni: a mar feldolgozott darabok hangja
 * addigra torlodott.
 */
function TestCallBadge({ huddleId, keepUntil }: { huddleId: string; keepUntil: string | null }) {
  const session = useSession();
  const isSuperadmin = session.data?.user.isSuperadmin ?? false;
  const [busy, setBusy] = useState(false);

  if (!keepUntil && !isSuperadmin) return null;

  const label = keepUntil
    ? `Teszthívás — a hang ${new Date(keepUntil).toLocaleDateString('hu-HU')}-ig megmarad`
    : 'Teszthívásnak jelölöm (a hang 30 napig megmarad az átirat-méréshez)';

  const className = [
    'flex items-center gap-1.5 rounded-pill border px-2.5 py-1 text-2xs font-semibold',
    keepUntil
      ? 'border-[var(--danger)] bg-[var(--danger-soft)] text-ink'
      : 'border-dashed border-border-strong text-ink-faint hover:text-ink',
  ].join(' ');

  if (!isSuperadmin) {
    return (
      <span title={label} className={className}>
        <Icon name="atirat" /> <span className={FEJ_FELIRAT}>Teszthívás</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      title={label}
      aria-pressed={Boolean(keepUntil)}
      disabled={busy}
      onClick={() => {
        setBusy(true);
        void setTestHuddle(huddleId, !keepUntil).finally(() => setBusy(false));
      }}
      className={className}
    >
      <Icon name="atirat" /> <span className={FEJ_FELIRAT}>Teszthívás</span>
    </button>
  );
}

// ─── Fejlec-gombok ────────────────────────────────────────────────────────────

/**
 * A fejlec-gombok FELIRATA: csak akkor, ha a fejlecbe tenyleg befer.
 *
 * Korabban az ablak szelessege (`sm:`) dontott, holott a fejlecben nyolc-kilenc
 * gomb all egymas mellett. Egy 1100 px-es hivasablakban mar nem fertek el: a
 * gombsor kitolta a cimet, es az idozito meg az "Átirat készül" egymasra
 * csuszott. Most a FEJLEC szelessege dont (container query); ha szuk, csak az
 * ikon marad, a felirat a buboreksugoban van. Fejlecen kivul (nincs `fej`
 * konteiner) a regi `sm:` szabaly ervenyes.
 */
const FEJ_FELIRAT = 'hidden sm:inline @max-[80rem]/fej:hidden!';

/**
 * A hivas fejlecenek gombjai (Ügyfél, Chat, Behívás).
 *
 * Sokaig csupasz feliratok voltak - nem latszott rajtuk, hogy gombok, es egymas
 * mellett egy mondatnak tuntek. Ikon + keret + allapot: a bekapcsolt panel
 * gombja kiemelve all, az olvasatlan chatet pedig egy pont jelzi.
 *
 * Keskeny ablakban csak az ikon marad - a felirat a buboreksugoban van.
 */
function HeaderAction({
  ref,
  icon,
  label,
  onClick,
  active = false,
  primary = false,
  badge = false,
  disabled = false,
}: {
  ref?: Ref<HTMLButtonElement>;
  icon: IconName;
  label: string;
  onClick: () => void;
  active?: boolean;
  primary?: boolean;
  badge?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-pressed={active}
      disabled={disabled}
      title={label}
      className={[
        'relative flex items-center gap-1.5 rounded-pill border px-3 py-1.5 text-xs font-semibold',
        disabled ? 'cursor-not-allowed opacity-50' : '',
        'transition-[background-color,border-color,color,scale] duration-(--duration-quick) active:scale-95',
        primary
          ? 'border-accent bg-accent text-accent-ink shadow-(--shadow-soft) hover:bg-accent-hover'
          : active
            ? 'border-accent bg-accent-soft text-accent'
            : 'border-border-soft bg-surface-raised text-ink-muted hover:border-border-strong hover:text-ink',
      ].join(' ')}
    >
      <Icon name={icon} />
      <span className={FEJ_FELIRAT}>{label}</span>
      {badge && (
        <span
          role="img"
          aria-label="új üzenet"
          className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full border-2 border-surface bg-[var(--danger)]"
        />
      )}
    </button>
  );
}

// ─── Lenemitottak ─────────────────────────────────────────────────────────────

/**
 * "X lenemitott" - nagy, piros, a hivas kozepen.
 *
 * Nem modalis: a hatter atlatszo es atkattinthato, a vezerlosav elerheto.
 * Csak a kartya fogja meg a kattintast - egy kattintas visszakapcsol, es a
 * Ctrl+M ugyanigy mukodik. Aki szandekosan nemitva marad, az "Értem"-mel
 * eltunteti.
 */
function MutedByOtherOverlay({
  by,
  onUnmute,
  onDismiss,
}: {
  by: string;
  onUnmute: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="pointer-events-none absolute inset-0 z-30 grid place-items-center bg-black/35 p-4">
      <div
        role="alertdialog"
        aria-label={`${by} lenémított`}
        className="pointer-events-auto flex w-full max-w-xs animate-fade-rise flex-col items-center gap-3 rounded-card border-2 border-[var(--danger)] bg-surface-raised px-6 py-5 text-center shadow-(--shadow-modal)"
      >
        <span
          aria-hidden
          className="grid size-24 place-items-center rounded-full bg-[var(--danger)] text-5xl text-white shadow-(--shadow-lift)"
        >
          <Icon name="mikrofonNema" />
        </span>
        <div>
          <p className="font-display text-lg font-semibold text-ink">Lenémítottak</p>
          <p className="text-sm text-ink-muted">
            <span className="font-medium text-ink">{by}</span> kikapcsolta a mikrofonodat — most nem
            hallanak.
          </p>
        </div>
        <button
          type="button"
          autoFocus
          onClick={onUnmute}
          className="w-full rounded-control bg-[var(--danger)] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          <Icon name="mikrofon" /> Mikrofon visszakapcsolása
        </button>
        <p className="text-2xs text-ink-faint">
          vagy <kbd className="rounded border border-border-soft px-1 font-sans">Ctrl</kbd>+
          <kbd className="rounded border border-border-soft px-1 font-sans">M</kbd> ·{' '}
          <button
            type="button"
            onClick={onDismiss}
            className="underline underline-offset-2 hover:text-ink"
          >
            Értem, maradok némítva
          </button>
        </p>
      </div>
    </div>
  );
}

// ─── Reakciok ─────────────────────────────────────────────────────────────────

/**
 * Felszallo reakciok.
 *
 * Nem uzenetek: nem kerulnek az elozmenybe, es par masodperc mulva eltunnek.
 * Egy hivas kozben nem bongeszni akar az ember, hanem egy mozdulattal jelezni.
 */
function FloatingReactions() {
  const { nameOf } = useUsers();
  const { reactions } = useHuddle();
  if (reactions.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-16 z-20 flex justify-center gap-3">
      {reactions.map((reaction) => (
        <div key={reaction.id} className="animate-fade-rise text-center">
          <span className="block text-3xl">{reaction.emoji}</span>
          <span className="text-2xs text-ink-muted">{nameOf(reaction.from, '').split(' ')[0]}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Behivas ──────────────────────────────────────────────────────────────────

/**
 * Behivas a csatorna tagjai kozul.
 *
 * A kort a CSATORNA TAGSAGA szuri - a szerver is ezt ellenorzi. Kulonben a
 * behivas megkerulne a privat csatornak jogosultsagat.
 */
/**
 * Behivas a hivasba.
 *
 * **BARKIT be lehet hivni**, nem csak a csatorna tagjait - a szerver is igy
 * szuri (`inviteToHuddle`). Ez a lista sokaig a csatorna tagsagara szukitett,
 * amitol egy DM-olelesben senki nem jelent meg benne: ott ugyanis a "tagsag"
 * eppen az a ket ember, aki mar bent van. A gomb ott volt, a lista ures - a
 * legrosszabb fajta funkcio.
 *
 * A behivas CSAK a hivasra szol: nem lesz csatornatag, az uzeneteket nem
 * latja, es a hivas vegen a jogosultsag megszunik.
 */
function InvitePanel({
  onClose,
  triggerRef,
}: {
  onClose: () => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
}) {
  const { nameOf } = useUsers();
  const panelRef = useRef<HTMLDivElement | null>(null);
  useDismiss(panelRef, onClose, { ignore: [triggerRef], returnFocus: triggerRef });
  const { huddle, invite } = useHuddle();
  const { users } = useUsers();
  const [query, setQuery] = useState('');

  /*
    CSAK a meg uton levo keres - nem a "kit hivtunk mar" emlekezete.

    Korabban egy helyi `sent` halmaz tartotta szamon, kire kattintottunk, es
    abbol SOSEM kerult ki senki. Aki elutasitotta a hivast, az visszakerult a
    listaba - de "hívjuk ✓" felirattal es letiltott gombbal, vagyis UJRA MAR
    NEM lehetett behivni. A panel arrol beszelt, mi tortent egyszer, nem
    arrol, mi IGAZ most.

    A valasz a szerver allapota: a resztvevo-lista valos idoben frissul
    (`huddle.updated`), tehat abbol pontosan latszik, kit hivunk eppen. Ez a
    halmaz csak a kattintas es a szerver valasza kozotti pillanatot fedi le.
  */
  const [sending, setSending] = useState<Set<string>>(new Set());

  const participants = huddle?.participants ?? [];
  const joined = new Set(participants.filter((p) => p.state === 'joined').map((p) => p.user.id));
  const invited = new Set(participants.filter((p) => p.state === 'invited').map((p) => p.user.id));

  /*
    Akit EPPEN HIVUNK, az bent marad a listaban - halvanyan.

    Korabban eltunt belole, es a felhasznalo nem latta, hogy elment-e a
    hivas. Igy vegig latszik, kire varunk; es amint elutasitja vagy belep,
    a sor magatol a helyes allapotba kerul.
  */
  const candidates = users
    .filter((user) => !user.isBot && !joined.has(user.id))
    .filter((user) => nameOf(user).toLowerCase().includes(query.trim().toLowerCase()))
    .slice(0, 8);

  const hivas = async (userId: string) => {
    setSending((current) => new Set(current).add(userId));
    try {
      await invite([userId]);
    } finally {
      // A valasz utan a resztvevo-lista mondja meg az allapotot, nem mi.
      setSending((current) => {
        const next = new Set(current);
        next.delete(userId);
        return next;
      });
    }
  };

  return (
    <div
      ref={panelRef}
      className="absolute right-3 top-full z-20 mt-1 w-72 rounded-card border border-border bg-surface-raised p-3 shadow-modal"
    >
      <input
        aria-label="Kolléga keresése"
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Kit hívjunk be?"
        className="w-full rounded-control border border-border bg-surface px-3 py-2 text-sm"
      />

      {candidates.length === 0 ? (
        <p className="mt-2 text-xs text-ink-faint">
          {query.trim() ? 'Nincs ilyen nevű kolléga.' : 'Mindenki bent van már.'}
        </p>
      ) : (
        <ul className="mt-2 max-h-72 space-y-0.5 overflow-y-auto">
          {candidates.map((user) => {
            const varunk = invited.has(user.id) || sending.has(user.id);

            return (
              <li key={user.id}>
                <button
                  type="button"
                  disabled={varunk}
                  onClick={() => void hivas(user.id)}
                  className="flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-left text-sm transition-colors hover:bg-surface-hover disabled:opacity-50"
                >
                  <Avatar
                    userId={user.id}
                    name={user.displayName}
                    avatarUrl={user.avatarUrl}
                    size="sm"
                  />
                  <span className="min-w-0 flex-1 truncate">{nameOf(user)}</span>
                  <span className="text-xs text-ink-faint">{varunk ? 'hívjuk…' : 'Behívás'}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        onClick={onClose}
        className="mt-2 text-2xs text-ink-muted underline hover:text-ink"
      >
        Bezárás
      </button>
    </div>
  );
}

/**
 * Egy eszkoz-csoport a valasztoban.
 *
 * A cim nem diszites: mikrofon es hangszoro egymas alatt, cimke nelkul,
 * osszekeverheto lenne - es pont az a hiba all elo ujra, amit meg akarunk
 * szuntetni.
 */
function DeviceGroup({
  title,
  icon,
  devices,
  activeId,
  onPick,
}: {
  title: string;
  icon: 'mikrofon' | 'huddle' | 'kamera';
  devices: { deviceId: string; label: string }[];
  activeId: string | null;
  onPick: (deviceId: string) => void;
}) {
  if (devices.length === 0) return null;

  return (
    <div className="[&+&]:mt-1.5 [&+&]:border-t [&+&]:border-border-soft [&+&]:pt-1.5">
      <p className="flex items-center gap-1.5 px-2 py-1 text-2xs font-semibold uppercase tracking-wide text-ink-faint">
        <Icon name={icon} size="sm" /> {title}
      </p>
      {devices.map((device) => (
        <button
          key={device.deviceId}
          type="button"
          onClick={() => onPick(device.deviceId)}
          aria-pressed={device.deviceId === activeId}
          className={`block w-full truncate rounded-control px-2 py-1 text-left text-xs transition-colors hover:bg-surface-hover ${
            device.deviceId === activeId ? 'text-accent' : 'text-ink-muted'
          }`}
        >
          <span aria-hidden="true">{device.deviceId === activeId ? '● ' : '○ '}</span>
          {device.label}
        </button>
      ))}
    </div>
  );
}

/**
 * ZAJSZURES: Ki / Normál / Erős (huddle/noise.ts).
 *
 * A kivalasztott gomb a TENYLEGESEN futo szintet mutatja: ha a modell ezen a
 * gepen nem indult el, a "Ki" vilagit, es ezt ki is mondjuk - nem hazudunk
 * egy be nem kapcsolt szurot.
 */
function NoiseChoice() {
  const { noiseLevel, setNoiseLevel } = useHuddle();
  const [busy, setBusy] = useState(false);

  return (
    <div className="mt-1.5 border-t border-border-soft pt-1.5">
      <p className="flex items-center gap-1.5 px-2 py-1 text-2xs font-semibold uppercase tracking-wide text-ink-faint">
        <Icon name="mikrofon" size="sm" /> Zajszűrés
      </p>
      <div role="radiogroup" aria-label="Zajszűrés" className="flex gap-1 px-2 pb-1">
        {NOISE_LEVELS.map((level) => (
          <button
            key={level}
            type="button"
            role="radio"
            aria-checked={noiseLevel === level}
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void setNoiseLevel(level).finally(() => setBusy(false));
            }}
            className={`flex-1 rounded-control border px-2 py-1 text-xs font-medium transition-colors disabled:opacity-60 ${
              noiseLevel === level
                ? 'border-accent bg-accent-soft text-accent'
                : 'border-border-soft text-ink-muted hover:bg-surface-hover hover:text-ink'
            }`}
          >
            {NOISE_LEVEL_LABEL[level]}
          </button>
        ))}
      </div>
      <p className="px-2 pb-1 text-2xs text-ink-faint">
        {noiseLevel === 'strong'
          ? 'A háttérbeszédet is elnyomja; a saját hangod kicsit tompább lehet.'
          : noiseLevel === 'normal'
            ? 'Billentyűzet, ventilátor, háttérzaj kiszűrése (a telefonos hívó hangján is).'
            : 'Csak a böngésző alap zajszűrése.'}
      </p>
    </div>
  );
}

/**
 * KIHANGOSITAS vagy FUL - telefonon egy erintessel.
 *
 * Amit a bongeszo megenged: ahol a hangkimenetek kozott lehet valasztani
 * (Android Chrome a "Speakerphone" es az "Earpiece" kimenetet kulon listazza), ott a
 * ketto kozott valtunk, nevuk alapjan felismerve. Ahol a bongeszo nem ad ilyet
 * (iPhone Safari: nincs kimenet-valasztas), ott NEM mutatunk latszolag mukodo
 * kapcsolot - megmondjuk, hol lehet allitani. A mikrofon a rendszer aktualis
 * bemenete (telefon, fejhallgato); az Eszkozok kozott az is valaszthato.
 */
const EARPIECE_NAME = /earpiece|receiver|handset|f[uü]l(hallgat)?/i;
const SPEAKER_NAME = /speaker|kihangos|loud/i;

function SpeakerChoice({
  outputDevices,
  activeOutputId,
  canChooseOutput,
  onPick,
}: {
  outputDevices: { deviceId: string; label: string }[];
  activeOutputId: string | null;
  canChooseOutput: boolean;
  onPick: (deviceId: string) => void;
}) {
  const earpiece = outputDevices.find((device) => EARPIECE_NAME.test(device.label));
  const speaker = outputDevices.find(
    (device) => SPEAKER_NAME.test(device.label) && device.deviceId !== earpiece?.deviceId,
  );

  if (!canChooseOutput || !earpiece || !speaker) {
    return (
      <p className="text-2xs text-ink-faint">
        {canChooseOutput
          ? 'A kihangosítást és a fület a telefon rendszere állítja (hívás közben a hangkimenet-választóban), vagy válassz alább egy hangszórót.'
          : 'Ez a böngésző nem tud hangkimenetet váltani — a kihangosítást, a fület és a fejhallgatót a telefon rendszer-beállításai állítják.'}
      </p>
    );
  }

  const options = [
    { device: speaker, label: 'Kihangosítás', icon: 'huddle' as const },
    { device: earpiece, label: 'Fül', icon: 'telefon' as const },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 gap-2" role="group" aria-label="Hangkimenet">
        {options.map(({ device, label, icon }) => {
          const on = device.deviceId === activeOutputId;
          return (
            <button
              key={device.deviceId}
              type="button"
              aria-pressed={on}
              onClick={() => onPick(device.deviceId)}
              className={`flex items-center justify-center gap-2 rounded-control border px-3 py-2.5 text-sm font-medium ${
                on
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-border-soft text-ink-muted hover:border-border-strong'
              }`}
            >
              <Icon name={icon} /> {label}
            </button>
          );
        })}
      </div>
      {activeOutputId === null && (
        <p className="mt-1.5 text-2xs text-ink-faint">Most a telefon alapértelmezése szól.</p>
      )}
    </div>
  );
}
// ─── Kozos ────────────────────────────────────────────────────────────────────

function Control({
  ref,
  label,
  active,
  onClick,
  grouped = false,
  narrow = false,
  disabled = false,
  children,
}: {
  ref?: Ref<HTMLButtonElement>;
  label: string;
  active?: boolean;
  onClick: () => void;
  /** Egy {@link ControlGroup} tagja: a keretet es a kerekitest a csoport adja. */
  grouped?: boolean;
  /** Keskeny gomb - az osztott gomb nyila. */
  narrow?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      ref={ref}
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      disabled={disabled}
      className={[
        'py-2 text-base transition-colors duration-(--duration-quick) disabled:cursor-wait disabled:opacity-50',
        narrow ? 'px-1.5' : 'px-3',
        grouped ? '' : 'rounded-control',
        active
          ? 'bg-accent-soft text-accent'
          : 'text-ink-muted hover:bg-surface-hover hover:text-ink',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

/** Gyorsszavazo gomb: zold pipa / piros X, megnyomva kitoltott. */
function QuickVoteButton({
  vote,
  active,
  onClick,
}: {
  vote: QuickVote;
  active: boolean;
  onClick: (vote: QuickVote) => void;
}) {
  const yes = vote === 'yes';
  const label = yes
    ? active
      ? 'Pipa visszavonása'
      : 'Egyetértek (pipa)'
    : active
      ? 'X visszavonása'
      : 'Nem értek egyet (X)';
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={() => onClick(vote)}
      className={[
        'px-3 py-2 text-base font-bold transition-colors duration-(--duration-quick)',
        active
          ? yes
            ? 'bg-[var(--success)] text-white'
            : 'bg-[var(--danger)] text-white'
          : yes
            ? 'text-[var(--success)] hover:bg-surface-hover'
            : 'text-[var(--danger)] hover:bg-surface-hover',
      ].join(' ')}
    >
      <Icon name={yes ? 'pipa' : 'bezaras'} />
    </button>
  );
}

/**
 * A GYORSSZAVAZAS ALLASA a hivas tetejen: hanyan nyomtak pipat, hanyan X-et.
 * Rámutatva a nevek is latszanak. Barki nullazhatja - a kovetkezo szobeli
 * kerdes tiszta lappal indul (es 2 perc csend utan magatol is eltunik).
 */
function QuickVoteTally({
  votes,
  nameFor,
  onClear,
}: {
  votes: [string, QuickVote][];
  nameFor: (identity: string) => string;
  onClear: () => void;
}) {
  const yes = votes.filter(([, vote]) => vote === 'yes').map(([id]) => nameFor(id));
  const no = votes.filter(([, vote]) => vote === 'no').map(([id]) => nameFor(id));

  return (
    <div
      role="status"
      aria-label={`Gyorsszavazás: ${yes.length} egyetért, ${no.length} nem ért egyet`}
      className="absolute left-1/2 top-3 z-20 flex -translate-x-1/2 items-center gap-2 rounded-pill border border-border-soft bg-surface-raised px-2 py-1.5 shadow-(--shadow-lift)"
    >
      <span
        title={yes.length ? `Egyetért: ${yes.join(', ')}` : 'Még senki'}
        className="flex items-center gap-1.5 rounded-pill bg-[var(--success)] px-3 py-1 text-lg font-bold text-white tabular-nums"
      >
        <Icon name="pipa" /> {yes.length}
      </span>
      <span
        title={no.length ? `Nem ért egyet: ${no.join(', ')}` : 'Még senki'}
        className="flex items-center gap-1.5 rounded-pill bg-[var(--danger)] px-3 py-1 text-lg font-bold text-white tabular-nums"
      >
        <Icon name="bezaras" /> {no.length}
      </span>
      <button
        type="button"
        onClick={onClear}
        title="A szavazás nullázása mindenkinél"
        className="rounded-pill px-2.5 py-1 text-xs font-semibold text-ink-muted hover:bg-surface-hover hover:text-ink"
      >
        Nullázás
      </button>
    </div>
  );
}

/** Osszetartozo vezerlogombok kozos keretben, valasztovonallal. */
function ControlGroup({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-stretch divide-x divide-border-soft overflow-hidden rounded-control border border-border-soft">
      {children}
    </div>
  );
}
